// Los Angeles, "Hollywood after dark": a warm October night on Hollywood Boulevard, looking north up a palm-lined side
// street to the Hollywood Hills, a dry Santa Ana breeze stirring the palms.
// Viewpoint: our own sidewalk on the boulevard's south side, facing north up a side street (invented, after Beachwood
// Drive's view of the sign). The order is the real one: Capitol Records a few blocks west (left), the Hollywood Sign on
// Mount Lee straight ahead, Griffith Observatory on its spur of Mount Hollywood to the north-east (right).
// A postcard, not a camera: the sign and the observatory are drawn about three times the size the hills give them (each
// keeps its own proportions), Capitol Records stands closer, and the three are composed together.
// Back to front: a dithered night sky, stars, a big moon over the observatory, two city-lit clouds, a plane -> the hazy
// far ridges -> Mount Lee with its antenna masts and the Hollywood Sign (a soft light walks along it once a minute),
// Hollywoodland's few lights, Griffith Observatory's floodlit front and copper domes on its knoll -> the police
// helicopter, the Bowl's fireworks and the premiere's searchlights -> the city's glow at the hills' foot -> Hollywood's
// mid-rises and Capitol Records (its beacon spelling HOLLYWOOD in Morse) -> the side street: cars up and down, lamps,
// Washingtonia palms swaying west -> the boulevard's north side: the record shop and its cat, the bus stop, the
// NIGHT DRIVE billboard on the roof, the Saturn diner (googie roof, pylon sign with Saturn's orbiting ring, red booths,
// the counter, a waitress pouring coffee), a red convertible at the curb, people on the Walk of Fame -> the boulevard:
// two lanes of traffic, the local bus, a cruising convertible, the LAPD, the neon's shine -> our own kerb with its big
// stars -> dry leaves blowing west -> vignette. The loop's timeline is in the block above renderFrame.
import { W, H, LOOP, TAU, frac, clamp, lerp, smooth, wave, step, env, inEvent, cycle, hash, rng, bayer, hex,
  makeCanvas, px, rect, ditherFill, makeHalo, drawHalo, text, textWidth, reflect, streak, makePitch, gradeAd } from '../engine/core.js';
import { weatherPlans, drawWeather } from '../engine/weather.js';

let ctx;                                          // this scene's 480x270 context, set by create()
const halo = (hl, x, y, a = 1) => drawHalo(ctx, hl, x, y, a);

/* ================= Layout ================= */
// The side street is drawn through a pinhole: eye 4.5 m over the boulevard, the street's vanishing point on the
// horizon. x = VPX + F X / Z, y = HZN + F (EYE - Y) / Z, in metres (X east of the street's centre line, Z north).
const HZN = 150, VPX = 256, F = 260, EYE = 4.5;
const proj = (X, Z, Y = 0) => [VPX + F * X / Z, HZN + F * (EYE - Y) / Z];
const Z0 = 20;                                    // the boulevard's north curb
const BLDG = 200;                                 // the north side's building line (the diner's floor)
const CURB = 208;                                 // the north curb's top; the boulevard below it
const MOON = { x: 446, y: 31, r: 8 };
const LEE = { x: 284, y: 23 };                    // Mount Lee's summit (the masts stand on it)
const SIGN = { x: 205, y: 38, h: 12 };            // the Hollywood Sign: its letters' top-left and height
const OBS = { cx: 400, base: 84, x0: 358, x1: 442 };   // Griffith Observatory's south front on its terrace
const CAP = { cx: 151, roof: 92, floors: 13, fh: 5, hw: 15 };   // Capitol Records: centre, roof row, floors, half-width
const DINER = { x0: 0, x1: 113, wall: 142, roof: 150, win0: 160, win1: 192 };
const PYLON = { x: 120, top: 78 };                // the diner's googie pylon sign
const SHOP = { x0: 362, x1: 480, top: 143 };      // the record shop on the right corner
const BB = { x: 398, y: 112, w: 48, h: 20 };      // the billboard's face on the shop's roof

/* ================= Pixel helpers ================= */
function painter(w, h) {
  const [c, x] = makeCanvas(w, h), img = x.createImageData(w, h), d = img.data, cache = {};
  const put = (i, y, col, a = 255) => {
    i = Math.round(i); y = Math.round(y);
    if (i < 0 || y < 0 || i >= w || y >= h) return;
    const rgb = cache[col] || (cache[col] = hex(col)), o = (y * w + i) * 4;
    d[o] = rgb[0]; d[o + 1] = rgb[1]; d[o + 2] = rgb[2]; d[o + 3] = a;
  };
  const alpha = (i, y) => (i < 0 || y < 0 || i >= w || y >= h) ? 0 : d[(y * w + i) * 4 + 3];
  return { c, x, put, alpha, done: () => { x.putImageData(img, 0, 0); return c; } };
}
function line(c, x0, y0, x1, y1, col) {
  x0 = Math.round(x0); y0 = Math.round(y0); x1 = Math.round(x1); y1 = Math.round(y1);
  const dx = Math.abs(x1 - x0), dy = -Math.abs(y1 - y0), sx = x0 < x1 ? 1 : -1, sy = y0 < y1 ? 1 : -1;
  let err = dx + dy;
  c.fillStyle = col;
  for (;;) {
    c.fillRect(x0, y0, 1, 1);
    if (x0 === x1 && y0 === y1) break;
    const e2 = 2 * err;
    if (e2 >= dy) { err += dy; x0 += sx; }
    if (e2 <= dx) { err += dx; y0 += sy; }
  }
}
function sprite(rows, key, flip = false) {
  const h = rows.length, w = Math.max(...rows.map(r => r.length)), p = painter(w, h);
  rows.forEach((row, y) => { for (let i = 0; i < row.length; i++) { const col = key[row[i]]; if (col) p.put(flip ? w - 1 - i : i, y, col); } });
  return p.done();
}
const tintCache = {};
function tint(col, tcol, a) {
  const k = col + tcol + a;
  if (!tintCache[k]) {
    const A = hex(col), B = hex(tcol);
    tintCache[k] = '#' + [0, 1, 2].map(j => Math.round(A[j] + (B[j] - A[j]) * clamp(a, 0, 1)).toString(16).padStart(2, '0')).join('');
  }
  return tintCache[k];
}
// pick from a ramp with ordered dither (v in 0..1)
function ramp(pal, v, i, y) {
  const f = clamp(v, 0, 1) * (pal.length - 1);
  let k = Math.floor(f);
  if (f - k > bayer(i, y)) k++;
  return pal[Math.min(k, pal.length - 1)];
}

/* ================= Sky ================= */
const SKY = ['#06071a', '#090b21', '#0d0f29', '#111432', '#16183b', '#1c1d44', '#23214d', '#2b2655', '#352a5c',
  '#402f61', '#4c3464', '#5a3a66', '#694066', '#784765', '#875062'];
function renderSky() {
  return ditherFill(W, H, SKY, (i, y) => {
    let v = Math.pow(Math.min(1, y / 172), 1.2) * 0.84;
    v += Math.max(0, 1 - Math.hypot((i - 250) / 320, (y - 175) / 125)) * 0.24;          // the city's glow over the basin
    v += Math.max(0, 1 - Math.hypot((i - MOON.x) / 120, (y - MOON.y) / 80)) * 0.12;      // moonlit haze
    return v;
  });
}
function renderMoon() {
  const r = MOON.r, s = r * 2 + 1, p = painter(s, s);
  const craters = [[-3, -2, 1.8], [2, 2, 1.6], [-1, 4, 1.2], [3, -4, 1.0], [-5, 2, 0.9], [1, -1, 0.8]];
  for (let y = 0; y < s; y++) for (let i = 0; i < s; i++) {
    const dx = i - r, dy = y - r, dd = Math.hypot(dx, dy);
    if (dd > r + 0.35) continue;
    let col = dx + dy < -6 ? '#fffbea' : dx + dy < 3 ? '#f6efd8' : '#e9dfc4';
    for (const [cx, cy, cr] of craters) if (Math.hypot(dx - cx, dy - cy) < cr) col = dx + dy < 0 ? '#e2d8bc' : '#d6cbae';
    if (dd > r - 1.1) col = dx + dy > 2 ? '#c8c2ac' : '#efe8d0';
    p.put(i, y, col);
  }
  return p.done();
}
const STARS = [];
{
  const r = rng(31);
  while (STARS.length < 46) {
    const x = Math.floor(r() * W), y = 3 + Math.floor(Math.pow(r(), 1.4) * 70);
    if (Math.hypot(x - MOON.x, y - MOON.y) < 22) continue;
    STARS.push({ x, y, k: 13 + Math.floor(r() * 70), ph: r(), b: 0.3 + r() * 0.55, big: r() < 0.08,
      col: r() < 0.75 ? '#e6eaff' : '#fff0d6' });
  }
}

/* ================= City-lit clouds (tileable; moonlit tops, bellies warm from the city) ================= */
const CLOUD_DEFS = [
  { y: 4, h: 30, k: 1, banks: [[70, 120, 17], [258, 70, 13]], seed: 5,
    pal: { top: '#3c3a66', body: '#262650', core: '#22224a', bot: '#5a4264', bot2: '#463a5e' } },
  { y: 20, h: 28, k: 2, banks: [[150, 90, 15], [380, 100, 13]], seed: 11,
    pal: { top: '#4a4470', body: '#2e2a56', core: '#2a2650', bot: '#7a4e66', bot2: '#5e4462' } },
];
function makeCloudLayer(def) {
  const r = rng(def.seed), h = def.h, puffs = [];
  for (const [bx, bw, base] of def.banks) {
    const np = 7 + Math.floor(r() * 4);
    for (let i = 0; i < np; i++) {
      const rx = 8 + r() * 13, ry = 3 + r() * 4.5;
      puffs.push({ x: bx + (r() - 0.5) * bw, y: base - r() * ry * 1.1, rx, ry, base: base + ry * 0.3 });
    }
  }
  const D = new Float32Array(W * h);
  for (let y = 0; y < h; y++) for (let x = 0; x < W; x++) {
    let best = -9;
    for (const p of puffs) {
      let dx = x - p.x; dx -= Math.round(dx / W) * W;
      const dy = y - p.y;
      let v = 1 - (dx * dx) / (p.rx * p.rx) - (dy * dy) / (p.ry * p.ry);
      if (y > p.base) v -= (y - p.base) * 0.45;
      if (v > best) best = v;
    }
    best += 0.1 * Math.sin(TAU * 9 * x / W + y * 0.37 + def.seed) * Math.sin(TAU * 4 * x / W - y * 0.21);
    D[y * W + x] = best;
  }
  const get = (x, y) => (y < 0 || y >= h) ? -9 : D[y * W + ((x % W) + W) % W];
  const p = painter(W, h), P = def.pal;
  for (let y = 0; y < h; y++) for (let x = 0; x < W; x++) {
    const th = (bayer(x, y) - 0.5) * 0.3, v = D[y * W + x];
    if (v + th <= 0.03) continue;
    let col;
    if (get(x, y - 1) + th <= 0.03 || get(x, y - 2) <= 0) col = P.top;
    else if (get(x, y + 1) + th <= 0.03) col = P.bot;
    else if (get(x, y + 3) <= 0.05 + bayer(x, y) * 0.2) col = P.bot2;
    else if (v > 0.55 + th) col = P.core;
    else col = P.body;
    p.put(x, y, col);
  }
  return { c: p.done(), y: def.y, k: def.k };
}

/* ================= The Hollywood Hills ================= */
// cosine interpolation through control points [x, y]
function through(pts, x) {
  if (x <= pts[0][0]) return pts[0][1];
  for (let k = 1; k < pts.length; k++) {
    const [xa, ya] = pts[k - 1], [xb, yb] = pts[k];
    if (x <= xb) { const u = (x - xa) / (xb - xa); return lerp(ya, yb, (1 - Math.cos(Math.PI * u)) / 2); }
  }
  return pts[pts.length - 1][1];
}
// the near ridge: the long western shoulder, Mount Lee's summit, the saddle, the observatory's terrace, the east slope
const RIDGE = [[0, 73], [36, 70], [70, 65], [104, 58], [136, 50], [166, 41], [196, 33], [228, 28], [256, 26], [274, 24],
  [284, 23], [294, 24], [306, 27], [320, 37], [334, 54], [346, 76], [354, 90], [360, 89], [365, 85], [367, 84], [435, 84],
  [440, 85], [450, 89], [464, 95], [480, 101]];
const TERRACE = [365, 436];                        // the observatory's terrace is flat
const ridgeY = x => {
  const y = through(RIDGE, x);
  return x >= TERRACE[0] && x <= TERRACE[1] ? y : y + 0.7 * Math.sin(x * 0.41) + 0.5 * Math.sin(x * 0.93 + 1.3);
};
// the far ridges in the haze: Cahuenga Peak (left, a little higher than Mount Lee) and Mount Hollywood (right)
const FAR_L = [[0, 57], [40, 51], [84, 45], [124, 38], [156, 31], [178, 25], [194, 21], [208, 23], [226, 29], [250, 36]];
const FAR_R = [[300, 70], [330, 66], [356, 69], [392, 73], [430, 70], [458, 64], [480, 61]];
const HILL = ['#0f0e20', '#131226', '#17152b', '#1c1830', '#211b34', '#271f38', '#2e233c'];   // dark crest .. city-warmed foot
// roads on the slopes, as polylines: the house lights sit along them, a few lit, most dark
const ROADS = [
  [[8, 140], [30, 134], [58, 130], [86, 124], [108, 118]], [[22, 120], [46, 112], [74, 106], [98, 98], [118, 92]],
  [[70, 92], [92, 84], [112, 78], [132, 72]], [[132, 136], [150, 128]],
  [[212, 134], [226, 118], [244, 108], [258, 96], [270, 86], [280, 76], [290, 68]],
  [[262, 132], [282, 116], [300, 104], [312, 92], [322, 82]], [[232, 142], [252, 132], [274, 126], [296, 120], [318, 114], [334, 108]],
  [[196, 120], [206, 104], [214, 92], [224, 80]], [[300, 140], [320, 130], [338, 124]],
  [[440, 132], [458, 124], [476, 118]], [[398, 142], [420, 136], [446, 140], [470, 134]], [[444, 112], [462, 106], [478, 104]],
];
// the road up to the observatory, its lamps every few metres of switchback
const OBS_ROAD = [[336, 148], [348, 138], [362, 132], [380, 126], [366, 118], [386, 110], [404, 104], [392, 98], [412, 92], [430, 88]];
const SIGN_BOX = [SIGN.x - 6, SIGN.y - 4, SIGN.x + 106, SIGN.y + 18];
const HOUSE = [], ROAD_LAMPS = [];
{
  const r = rng(71);
  const inSign = (x, y) => x >= SIGN_BOX[0] && x <= SIGN_BOX[2] && y >= SIGN_BOX[1] && y <= SIGN_BOX[3];
  for (const road of ROADS) for (let k = 1; k < road.length; k++) {
    const [xa, ya] = road[k - 1], [xb, yb] = road[k], n = Math.max(1, Math.round(Math.hypot(xb - xa, yb - ya) / 5));
    for (let j = 0; j < n; j++) {
      const u = (j + r()) / n, x = Math.round(lerp(xa, xb, u) + (r() - 0.5) * 5), y = Math.round(lerp(ya, yb, u) + (r() - 0.5) * 5);
      if (inSign(x, y) || y <= ridgeY(x) + 3 || r() < (x < 190 ? 0.72 : 0.5)) continue;
      const v = r();
      HOUSE.push({ id: HOUSE.length, x, y, w: v < 0.18 ? 2 : 1, tw: v > 0.7, k: 9 + Math.floor(r() * 40), ph: r(),
        col: v < 0.5 ? '#ffcf7e' : v < 0.8 ? '#ffb45e' : v < 0.93 ? '#ffe6b0' : '#cfe0ff' });
    }
  }
  for (let k = 1; k < OBS_ROAD.length; k++) {
    const [xa, ya] = OBS_ROAD[k - 1], [xb, yb] = OBS_ROAD[k], n = Math.max(1, Math.round(Math.hypot(xb - xa, yb - ya) / 11));
    for (let j = 0; j < n; j++) if (hash(k, j, 77) < 0.7) ROAD_LAMPS.push({ x: Math.round(lerp(xa, xb, j / n)), y: Math.round(lerp(ya, yb, j / n)) });
  }
}
function renderHills() {
  const p = painter(W, H);
  // far ridges, hazy violet with a moonlit rim
  for (const [pts, x0, x1] of [[FAR_L, 0, 250], [FAR_R, 300, W - 1]]) for (let i = x0; i <= x1; i++) {
    const top = Math.round(through(pts, i) + 0.6 * Math.sin(i * 0.5));
    for (let y = top; y < 160; y++) p.put(i, y, y === top ? '#24234a' : '#1a1936');
  }
  // the near hills
  for (let i = 0; i < W; i++) {
    const top = Math.round(ridgeY(i)), facing = ridgeY(i + 1) - ridgeY(i - 1);   // > 0: the slope faces the moon (east)
    for (let y = top; y < 168; y++) {
      const dd = y - top;
      let v = smooth(top + 20, 150, y) * 0.86;                                   // warmer towards the lit city below
      const lit = 1 - Math.hypot((i - (SIGN.x + 50)) / 74, (y - SIGN.y - 9) / 22);   // the floodlight's pool round the sign
      if (lit > 0) v += lit * 0.42;
      const ob = 1 - Math.hypot((i - OBS.cx) / 56, (y - OBS.base - 2) / 12);           // the observatory's lights spill on its knoll
      if (ob > 0 && y > OBS.base) v += ob * 0.5;
      if (hash(i >> 1, y >> 1, 7) < 0.14 && dd > 3) v -= 0.12;                    // chaparral clumps
      let col = ramp(HILL, v, i, y);
      if (dd === 0) col = facing > 0.25 ? '#4a466e' : facing > -0.25 ? '#34315a' : '#26243f';     // moonlit crest
      else if (dd === 1 && facing > 0.25 && bayer(i, y) < 0.6) col = '#2e2b4e';
      p.put(i, y, col);
    }
  }
  // Beachwood Canyon and two gullies: shaded folds running down from the ridge
  for (const [xa, ya, xb, yb] of [[318, 40, 292, 132], [176, 48, 150, 128], [92, 70, 70, 132], [446, 92, 470, 146]]) {
    const n = Math.round(Math.hypot(xb - xa, yb - ya));
    for (let k = 0; k <= n; k++) {
      const u = k / n, x = Math.round(lerp(xa, xb, u) + 3 * Math.sin(u * 7)), y = Math.round(lerp(ya, yb, u));
      if (bayer(x, y) < 0.75 - u * 0.4) { p.put(x, y, '#0c0b1a'); p.put(x + 1, y, '#121124'); }
    }
  }
  // oaks and pines in the canyons near the houses: dark clumps, the city's light on their lower edge
  const r = rng(73);
  for (let n = 0; n < 34; n++) {
    const cx = r() * W, cy = 96 + r() * 54, rx = 2 + r() * 3, ry = 1.6 + r() * 2;
    if (cx > SIGN_BOX[0] - 4 && cx < SIGN_BOX[2] + 4 && cy < SIGN_BOX[3] + 6) continue;
    if (cy < ridgeY(cx) + 6) continue;
    for (let y = Math.floor(cy - ry); y <= cy + ry; y++) for (let i = Math.floor(cx - rx); i <= cx + rx; i++) {
      const d = Math.hypot((i - cx) / rx, (y - cy) / ry);
      if (d < 1 && d < 0.75 + bayer(i, y) * 0.35) p.put(i, y, y > cy + ry * 0.4 ? '#2a1e2e' : '#0d0c19');
    }
  }
  // dark houses along the roads (their walls), then the lit windows
  for (const h of HOUSE) { p.put(h.x - 1, h.y, '#2a2234'); p.put(h.x + h.w, h.y, '#241e30'); p.put(h.x, h.y + 1, '#1c1828'); }
  for (const h of HOUSE) if (!h.tw) for (let k = 0; k < h.w; k++) p.put(h.x + k, h.y, h.col);
  for (const l of ROAD_LAMPS) { p.put(l.x, l.y, '#e8c890'); p.put(l.x, l.y + 1, '#3a2c34'); }
  // Mount Lee's summit: the low equipment house and two lattice masts
  const { x: lx, y: ly } = LEE, MAST = '#1e1d34', MAST_L = '#40405e';
  for (let i = lx - 7; i <= lx + 6; i++) for (let y = ly - 3; y < ly + 1; y++) p.put(i, y, y === ly - 3 ? '#4a4870' : i > lx + 3 ? '#1a1930' : '#2a2946');
  for (let y = ly - 20; y < ly - 3; y++) {
    const wide = y > ly - 12;
    p.put(lx + 2, y, MAST); if (wide) p.put(lx + 3, y, ((y + lx) & 1) ? MAST_L : MAST);
    if (wide && (y & 3) === 0) { p.put(lx + 1, y, MAST); p.put(lx + 4, y, MAST); }
  }
  for (let y = ly - 12; y < ly - 3; y++) p.put(lx - 5, y, (y & 1) ? MAST : MAST_L);
  return p.done();
}

/* ================= The Hollywood Sign ================= */
// Nine sheet-metal letters, 45 ft tall and 31 to 39 ft wide, 352 ft end to end: 12 rows and about 100 columns here
// (12 : 100 = 0.12, the real 45 : 352 = 0.13; the H 12 : 9 = 1.33, the real 45 : 33 = 1.36). Each letter is built from
// thick strokes and stands a row up or down with the slope, as the real ones do.
const LETTERS = {
  H: { w: 9, s: [[1.2, 0, 1.2, 11], [7.8, 0, 7.8, 11], [1.2, 5.5, 7.8, 5.5]] },
  O: { w: 10, ring: [4.5, 5.5, 4.8, 6.0, 2.2, 3.3] },
  L: { w: 8, s: [[1.2, 0, 1.2, 11], [1.2, 10.2, 7, 10.2]] },
  Y: { w: 10, s: [[1.3, 0, 4.5, 5.6], [7.7, 0, 4.5, 5.6], [4.5, 5.4, 4.5, 11]] },
  W: { w: 12, s: [[1.1, 0, 3.0, 11], [3.0, 11, 5.5, 1.5], [5.5, 1.5, 8.0, 11], [8.0, 11, 9.9, 0]] },
  D: { w: 9, s: [[1.2, 0, 1.2, 11]], half: [1.2, 5.5, 7.0, 6.0, 4.5, 3.3] },
};
const SIGN_WORD = 'HOLLYWOOD', SIGN_DROP = [1, 0, 0, 1, 1, 0, 0, 1, 0], SIGN_GAP = [2, 1, 2, 2, 1, 2, 1, 2];
const SIGN_LETTERS = [];
{
  let x = SIGN.x;
  for (let k = 0; k < 9; k++) { const L = LETTERS[SIGN_WORD[k]]; SIGN_LETTERS.push({ ch: SIGN_WORD[k], x, y: SIGN.y + SIGN_DROP[k], w: L.w }); x += L.w + (SIGN_GAP[k] || 0); }
}
const SIGN_X1 = SIGN_LETTERS[8].x + SIGN_LETTERS[8].w - 1;
function letterAt(L, i, j) {                       // is pixel (i, j) of the letter covered? (centre of the pixel)
  const px0 = i + 0.5 - 0.5, py0 = j;
  const R = 1.25;
  if (L.s) for (const [ax, ay, bx, by] of L.s) {
    const dx = bx - ax, dy = by - ay, l2 = dx * dx + dy * dy;
    const u = l2 ? clamp(((px0 - ax) * dx + (py0 - ay) * dy) / l2, 0, 1) : 0;
    if (Math.hypot(px0 - ax - u * dx, py0 - ay - u * dy) <= R) return true;
  }
  if (L.ring) {
    const [cx, cy, ox, oy, ix, iy] = L.ring, a = ((px0 - cx) / ox) ** 2 + ((py0 - cy) / oy) ** 2, b = ((px0 - cx) / ix) ** 2 + ((py0 - cy) / iy) ** 2;
    if (a <= 1 && b > 1) return true;
  }
  if (L.half) {
    const [cx, cy, ox, oy, ix, iy] = L.half;
    if (px0 >= cx) { const a = ((px0 - cx) / ox) ** 2 + ((py0 - cy) / oy) ** 2, b = ((px0 - cx) / ix) ** 2 + ((py0 - cy) / iy) ** 2; if (a <= 1 && b > 1) return true; }
  }
  return false;
}
function renderSign() {
  const w = SIGN_X1 - SIGN.x + 3, h = SIGN.h + 4, p = painter(w, h);
  for (const S of SIGN_LETTERS) {
    const L = LETTERS[S.ch], on = (i, j) => i >= 0 && i < L.w && j >= 0 && j < SIGN.h && letterAt(L, i, j);
    for (let j = 0; j < SIGN.h; j++) for (let i = 0; i < L.w; i++) {
      if (!on(i, j)) continue;
      let col = j < 2 ? '#f4f0ec' : j < 8 ? '#fffaf0' : '#fff2dc';                  // a soft light from below, warm at the foot
      if (!on(i + 1, j)) col = j < 6 ? '#cfc8cc' : '#d8ccc4';                       // the steps' shaded right edges
      else if (!on(i, j - 1)) col = '#ffffff';                                       // top edges catch the most
      if (j === SIGN.h - 1) col = on(i + 1, j) ? '#e8dccb' : '#c4b8b2';
      p.put(S.x - SIGN.x + i, S.y - SIGN.y + j, col);
    }
    // the scaffold's legs under each letter, dark on the slope
    for (const lx of [2, L.w - 3]) { p.put(S.x - SIGN.x + lx, S.y - SIGN.y + SIGN.h, '#3a3448'); p.put(S.x - SIGN.x + lx, S.y - SIGN.y + SIGN.h + 1, '#2a2638'); }
  }
  return p.done();
}

/* ================= Griffith Observatory: the south front, floodlit ================= */
// 236 ft east to west, the planetarium's copper dome 82 ft across with its top 77 ft over the lawn, the two telescope
// domes' tops 55 ft. Here 84 px wide: the main dome 29 px across (29 : 84 = 0.35, real 0.35), its top 28 px up
// (28 : 84 = 0.33, real 0.33), the small domes' tops 19 px up (19 : 28 = 0.68, real 0.71).
const WALL = ['#55526a', '#76728a', '#9893a4', '#b6b0ba', '#cec8c8', '#ded6ce', '#e8e0d4', '#f0e8dc'];
const COPPER = ['#2e1c16', '#462a1e', '#623a24', '#80502e', '#9e6638', '#ba7e46', '#d49a5e', '#e8b67c'];
const OBS_PARTS = [                                // [x0, x1, top] of each block, back to front
  [358, 370, 71], [430, 442, 71],                  // end towers under the telescope domes
  [371, 385, 75], [415, 429, 75],                  // the wings
  [386, 414, 70],                                  // the central block under the drum
  [392, 408, 72],                                  // the entrance pavilion, in front
];
const DOMES = [{ cx: 400, cy: 68, rx: 14.5, ry: 12.5, ribs: true }, { cx: 364, cy: 70, rx: 5.5, ry: 5.5 }, { cx: 436, cy: 70, rx: 5.5, ry: 5.5, slit: true }];
// one pixel of a copper dome: floodlit from the lawn on the left, the moon on its crown, ribs on the big one; the east
// dome's shutter is a slit at u = su (across its face, -1..1) of half-width sw, opened to `open` (0 shut .. 1 wide)
function domeColor(D, i, y, su, sw, open) {
  const u = (i + 0.5 - D.cx) / D.rx, v = (D.cy - y - 0.5) / D.ry, q = u * u + v * v;
  if (q > 1) return null;
  const nz = Math.sqrt(1 - q);
  let l = 0.12 + 0.7 * Math.max(0, -0.38 * u - 0.32 * v + 0.87 * nz) * (1 - 0.55 * v)   // floodlit from the lawn, left
    + 0.32 * Math.max(0, 0.55 * u + 0.6 * v + 0.58 * nz) * v;                           // the moon on the crown
  if (D.ribs) {                                                                        // the ribs, 20 degrees apart
    const lon = Math.atan2(u, Math.max(0.05, nz)) * 180 / Math.PI;
    if (Math.abs(((lon + 190) % 20) - 10) < 1.6 * (1 + Math.abs(u) * 2) && v < 0.9) l -= 0.16;
  }
  if (q > 0.82 && u > 0.2) l -= 0.1;                                                  // the limb in shadow
  if (D.slit && Math.abs(u - su) < sw + open * 0.24 && v > 0.15 - open * 0.15) return open > 0.3 ? (v < 0.5 && Math.abs(u - su) < 0.1 ? '#5a1a1e' : '#120e16') : ramp(COPPER, 0.06, i, y);
  return ramp(COPPER, l, i, y);
}
function renderObservatory() {
  const X0 = 346, Y0 = 50, p = painter(110, 44), put = (x, y, col) => p.put(x - X0, y - Y0, col), B = OBS.base;
  OBS_PARTS.forEach(([x0, x1, top], k) => {
    const front = k === 5;
    for (let y = top; y < B; y++) for (let i = x0; i <= x1; i++) {
      let v = 0.58 + 0.34 * smooth(top, B, y) + (front ? 0.08 : 0);                // the floodlights from the lawn
      if (i === x0) v += 0.2; else if (i === x1) v -= 0.36; else if (i === x1 - 1) v -= 0.14;
      if (y === top) v = 0.96; else if (y === top + 1) v = 0.42;                   // the cornice and its shadow line
      if (!front && k < 4 && (i - x0) % 3 === 2 && y > top + 2) v -= 0.12;         // Art Deco pilasters
      put(i, y, ramp(WALL, v, i, y));
    }
    if (k < 2) {                                                                   // a tall slit window in each tower
      const cx = (x0 + x1) >> 1;
      for (let y = top + 4; y < B - 3; y++) { put(cx, y, '#3a3448'); put(cx + 1, y, '#2a2638'); }
    }
    if (k === 2 || k === 3) for (let i = x0 + 2; i < x1 - 1; i += 4) { put(i, B - 6, '#5a5268'); put(i, B - 5, '#4a4458'); }
  });
  // the entrance: a lit doorway with the Art Deco fins either side
  for (let y = 76; y < B; y++) for (let i = 398; i <= 402; i++) put(i, y, y < 78 ? '#ffe8b8' : (i === 398 || i === 402) ? '#c8925a' : '#ffd08a');
  for (const i of [394, 396, 404, 406]) for (let y = 74; y < B - 1; y++) put(i, y, '#cfc6c4');
  // the drum under the main dome
  for (let i = 387; i <= 413; i++) { put(i, 68, i < 390 ? '#fff4e6' : '#e2d8d0'); put(i, 69, i > 410 ? '#7a7488' : '#a49eaa'); }
  for (const D of DOMES) {
    for (let y = Math.floor(D.cy - D.ry); y < D.cy; y++) for (let i = Math.floor(D.cx - D.rx); i <= Math.ceil(D.cx + D.rx); i++) {
      const col = domeColor(D, i, y, 0.18, 0.12, 0);
      if (col) put(i, y, col);
    }
    const fy = Math.floor(D.cy - D.ry) + (D.ribs ? 0 : 1);
    put(Math.round(D.cx - 0.5), fy, D.ribs ? '#c48a54' : '#9e6638'); if (D.ribs) put(Math.round(D.cx - 0.5), fy - 1, '#e8b67c');   // the finial
  }
  // the terrace's parapet and its wall
  for (let i = 355; i <= 445; i++) { put(i, B, i < 358 || i > 442 ? '#6a6680' : '#bcb4b8'); put(i, B + 1, '#5a5670'); for (let y = B + 2; y < B + 2 + (i < 360 || i > 440 ? 1 : 3); y++) put(i, y, '#34304a'); }
  return { c: p.done(), x: X0, y: Y0 };
}

/* ================= Capitol Records ================= */
// The round tower, 13 floors under wide curved awnings (the stack of records), the rooftop drum and the spire whose red
// light blinks HOLLYWOOD in Morse. Here 31 px across the awnings and 65 px to the roof (2.1 : 1; from photos about
// 2 : 1), the spire 26 px (0.4 of the tower; from photos about 0.4). Seen from the street below, each awning's round
// edge sags a row at its ends.
const CAP_LIT = ['#ffd890', '#f6c070', '#ffe6b0', '#c8dcff'];
function renderCapitol() {
  const { cx, roof, floors, fh, hw } = CAP, X0 = cx - hw - 1, Y0 = roof - 32, p = painter(hw * 2 + 3, floors * fh + 36);
  const put = (x, y, col) => p.put(x - X0, y - Y0, col), r = rng(52);
  const FAS = ['#4a4660', '#6e6880', '#948da0', '#bab2bc', '#dcd4d4', '#f2eae2'];
  for (let f = 0; f < floors; f++) {
    const y = roof + f * fh;
    for (let i = -hw; i <= hw; i++) {
      const u = i / hw, end = Math.abs(i) >= hw - 3 ? 1 : 0;
      const v = 0.25 + 0.75 * Math.max(0, Math.cos((u + 0.3) * 1.25)) - (Math.abs(i) === hw ? 0.2 : 0);
      put(cx + i, y + end, ramp(FAS, v, cx + i, y));                               // the awning's edge
      put(cx + i, y + 1 + end, u > 0.5 ? '#1a1828' : '#2a2638');                   // its underside, in shadow
    }
    for (let k = 0; k < 3; k++) for (let i = -hw + 2; i <= hw - 2; i++) {           // the glass band behind the awning
      const yy = y + 2 + k, u = i / hw;
      let col = (i & 1) ? '#14121e' : u > 0.55 ? '#1c1a2a' : '#262236';
      if (!(i & 1) && k < 2 && Math.abs(u) < 0.8) {
        const lit = hash(i >> 1, f, 53) < (u < 0.4 ? 0.62 : 0.4);
        if (lit) col = CAP_LIT[Math.floor(hash(i >> 1, f, 54) * 4)];
      }
      if (k === 2) col = (i & 1) ? '#100e18' : '#1a1826';
      put(cx + i, yy, col);
    }
  }
  // the roof: the top floor's cap, a drum, the spire
  for (let i = -hw + 1; i <= hw - 1; i++) put(cx + i, roof - 1, i < 4 ? '#d0c8cc' : '#8a8498');
  for (let y = roof - 5; y < roof - 1; y++) for (let i = -5; i <= 5; i++) put(cx + i, y, y === roof - 5 ? '#c8c0c8' : i < -3 ? '#8e889c' : i > 2 ? '#2e2a3e' : '#4a4660');
  for (let y = roof - 31; y < roof - 5; y++) {
    const base = y > roof - 11;
    put(cx, y, y < roof - 26 ? '#d8d4e0' : '#a8a2b4');
    if (base) { put(cx - 1, y, '#c8c2cc'); put(cx + 1, y, '#4a4660'); }
  }
  for (let i = -2; i <= 2; i++) put(cx + i, roof - 12, '#8a8498');                   // a ring on the mast
  return { c: p.done(), x: X0, y: Y0 };
}

/* ================= Hollywood's mid-rises at the foot of the hills: lit window grids ================= */
// [x0, width, top, style]: apartment blocks and hotels along Franklin Avenue, an Art Deco tower with a stepped crown
const FLATS = [[0, 20, 121], [20, 17, 129], [38, 26, 110, 'deco'], [64, 14, 127], [78, 52, 131], [172, 26, 136], [196, 30, 141],
  [226, 18, 144], [290, 20, 143], [308, 28, 139], [360, 22, 127], [382, 18, 133], [454, 26, 122], [458, 22, 117, 'deco']];
const FLAT_WINS = [];
function renderFlats() {
  const p = painter(W, H), r = rng(61), WARMW = ['#ffd890', '#f6c070', '#e8a85a'], COOLW = ['#c8dcff', '#a8bce8'];
  for (const [x0, w, top, style] of FLATS) {
    const x1 = x0 + w - 1, tone = r();
    let t0 = top;
    if (style === 'deco') {                                   // setbacks and a mast with a beacon
      const m = x0 + (w >> 1);
      for (const [dx, dy] of [[4, -5], [7, -9]]) for (let y = top + dy; y < top + dy + 4 + (dy === -5 ? 1 : 0); y++) for (let i = x0 + dx; i <= x1 - dx; i++)
        p.put(i, y, y === top + dy ? '#4a4266' : i === x0 + dx ? '#2e2848' : '#1c1830');
      for (let y = top - 18; y < top - 9; y++) p.put(m, y, '#3a3450');
      FLAT_WINS.push({ beacon: true, x: m, y: top - 19 });
      t0 = top;
    }
    for (let y = t0; y < 156; y++) for (let i = x0; i <= x1; i++) {
      let col = tone < 0.5 ? '#1c1830' : '#211a32';
      if (i === x0) col = '#2e2846'; else if (i === x1) col = '#120f20';
      if (y === t0) col = '#433a5e';
      p.put(i, y, col);
    }
    for (let wy = t0 + 3; wy < 150; wy += 3) {
      const full = r() < 0.06;
      for (let wx = x0 + 2; wx < x1 - 1; wx += 2) {
        const v = r();
        if (full) { p.put(wx, wy, WARMW[1]); continue; }
        if (v < 0.26) {
          const col = r() < 0.72 ? WARMW[Math.floor(r() * 3)] : COOLW[Math.floor(r() * 2)];
          if (r() < 0.2) FLAT_WINS.push({ x: wx, y: wy, col, n: 3 + Math.floor(r() * 7), off: r(), id: FLAT_WINS.length });
          else p.put(wx, wy, col);
        } else if (v < 0.4) p.put(wx, wy, '#2a2440');
      }
    }
  }
  return p.done();
}

/* ================= The side street: bungalows, lamps and palms receding to the hills ================= */
// lots along each side, [side, Z from, Z to, height in metres, stucco colour]
const LOTS = [];
{
  const r = rng(91), COLS = ['#3e3242', '#46363e', '#383446', '#4a3c3a', '#3c3040'];
  for (const side of [-1, 1]) {
    let z = 21 + r() * 2;
    while (z < 260) {
      const len = 9 + r() * 10, tall = z > 40 && r() < 0.3;
      LOTS.push({ side, za: z, zb: z + len, ht: tall ? 6.8 + r() * 1.5 : 3.3 + r() * 1, col: COLS[Math.floor(r() * COLS.length)],
        win: r() < 0.65, seed: Math.floor(r() * 1000) });
      z += len + (r() < 0.25 ? 2 + r() * 3 : 0.4);
    }
  }
}
const AV_LAMPS = [26, 44, 68, 100, 144, 200].flatMap(z => [{ X: -4.6, z }, { X: 4.6, z }]);
function renderAvenue() {
  const p = painter(W, H), ASPH = ['#100f18', '#15131d', '#1a1722', '#211c28', '#2a222e'], WALK = ['#1e1b26', '#272230', '#30293a', '#3c3240'];
  const pool = (X, Z) => {                                       // light from the avenue's lamps on the ground
    let v = 0;
    for (const l of AV_LAMPS) { const d = Math.hypot((X - l.X) / 5, (Z - l.z) / 7); if (d < 1) v = Math.max(v, (1 - d) * (1 - d)); }
    return v;
  };
  for (let y = HZN + 1; y <= CURB; y++) {
    const Z = F * EYE / (y - HZN);
    for (let i = 0; i < W; i++) {
      const X = (i + 0.5 - VPX) * Z / F, aX = Math.abs(X);
      if (aX > 9.2) continue;
      const fade = smooth(30, 160, Z) * 0.5, L = pool(X, Z);
      let col;
      if (aX < 4) {
        col = ramp(ASPH, 0.3 + L * 0.9 - fade * 0.3, i, y);
        if (aX < 0.09 * Math.max(1, 40 / Z) && frac(Z / 6) < 0.5) col = '#8a7440';    // the dashed centre line
      } else if (aX < 4.25) col = L > 0.2 ? '#6a5e66' : '#4a4252';                     // kerb
      else col = ramp(WALK, 0.35 + L * 0.8 - fade * 0.3, i, y);
      p.put(i, y, col);
    }
  }
  // the bungalows and shops along both sides, facades on the planes X = -9 and X = 9: stucco washed warm from the street's
  // lamps below, a darker cornice under the parapet, lit windows with a jamb on their far side, awnings over some shops
  for (const L of LOTS) {
    const s = L.side, xa = VPX + s * F * 9 / L.za, xb = VPX + s * F * 9 / L.zb;
    const shop = L.ht < 5 && L.seed % 3 === 0, aw = ['#5a2228', '#1e4a3a', '#3a2a5a'][L.seed % 3];
    for (let i = Math.round(Math.min(xa, xb)); i <= Math.round(Math.max(xa, xb)); i++) {
      if (i < 0 || i >= W) continue;
      const Z = s * F * 9 / (i + 0.5 - VPX);
      if (!(Z >= L.za && Z < L.zb)) continue;
      const yt = Math.round(HZN + F * (EYE - L.ht) / Z), yb = Math.round(HZN + F * EYE / Z), depth = smooth(25, 220, Z);
      const u = (Z - L.za) / (L.zb - L.za), edge = u < 0.04 || u > 0.97;
      for (let y = yt; y <= yb; y++) {
        const Y = EYE - (y + 0.5 - HZN) * Z / F;
        const lit = 0.3 + 0.5 * clamp(1 - Y / L.ht, 0, 1) - (edge ? 0.2 : 0);
        let col = ramp([tint(L.col, '#0c0a14', 0.4), L.col, tint(L.col, '#c8906a', 0.25)], lit, i, y);
        if (y === yt) col = '#8a7480';                                               // the parapet's lit edge
        else if (Y > L.ht - 0.45) col = tint(L.col, '#0c0a14', 0.5);                 // the cornice's shadow
        else if (Y < 0.3) col = '#1a1622';
        const wu = frac(u * (L.ht > 5 ? 3 : 2)), w2 = frac(u * 3);
        const inWin = (Y > 0.9 && Y < 2.3 && wu > 0.25 && wu < 0.75) || (L.ht > 5 && Y > 4.2 && Y < 5.6 && w2 > 0.3 && w2 < 0.7);
        if (inWin) {
          const on = L.win && hash(L.seed, Math.floor(u * 3), Y > 3 ? 1 : 0) < 0.7;
          col = on ? (Y > 2 ? '#ffd890' : '#f6c070') : '#1c1826';
          if (s * (wu - 0.5) > 0.18) col = on ? '#b07a4a' : '#141220';                // the window's far jamb
        }
        if (shop && Y > 2.35 && Y < 2.85) col = (Math.floor(u * 9) & 1) ? aw : tint(aw, '#ffffff', 0.25);   // an awning
        p.put(i, y, tint(col, '#3a2a40', depth * 0.55));
      }
    }
  }
  // cars parked along the kerbs (the two on the move are drawn per frame)
  for (const [Xc, z, col] of [[-3.1, 27, '#4a4e62'], [-3.1, 44, '#6a2a30'], [-3.1, 78, '#4a4a44'], [-3.1, 124, '#3a4a4a'],
    [3.1, 33, '#6a5a48'], [3.1, 62, '#3a3e52'], [3.1, 98, '#5a3438']]) avCar(p.put, Xc, z, 4.4, col, 'parked');
  return p.done();
}
// a car on the side street, seen from behind or ahead in perspective: the side that faces the street's middle and the
// near end (tail lights for one going away, headlights for one coming, dim red for a parked one); put(i, y, colour)
function avCar(put, Xc, z, len, col, kind) {
  const hw = 0.85, sx = Xc < 0 ? Xc + hw : Xc - hw;
  const x0 = Math.round(VPX + F * sx / (z + len)), x1 = Math.round(VPX + F * sx / z);
  for (let i = Math.min(x0, x1); i <= Math.max(x0, x1); i++) {
    const Z = F * sx / (i + 0.5 - VPX);
    if (!(Z >= z && Z <= z + len)) continue;
    const v = (Z - z) / len;
    for (let y = Math.round(HZN + F * (EYE - 1.42) / Z); y <= Math.round(HZN + F * (EYE - 0.12) / Z); y++) {
      const Y = EYE - (y + 0.5 - HZN) * Z / F, wheel = Y < 0.45 && (Math.abs(v - 0.18) < 0.1 || Math.abs(v - 0.82) < 0.1);
      if (Y > 1.0 && (v < 0.25 || v > 0.85)) continue;                              // the cabin is shorter than the body
      put(i, y, wheel ? '#08070c' : Y > 1.0 ? '#2a3448' : Y > 0.92 ? tint(col, '#ffffff', 0.18) : tint(col, '#000000', 0.35));
    }
  }
  const [ra] = proj(Xc - hw, z), [rb] = proj(Xc + hw, z), s0 = F / z;
  for (let i = Math.round(ra); i <= Math.round(rb); i++) for (let y = Math.round(HZN + F * (EYE - 1.42) / z); y <= Math.round(HZN + F * (EYE - 0.12) / z); y++) {
    const X = (i + 0.5 - VPX) / s0, Y = EYE - (y + 0.5 - HZN) / s0;
    if (Y > 1.0 && Math.abs(X - Xc) > 0.62) continue;
    let c2 = Y > 1.02 ? '#202838' : Y > 0.92 ? tint(col, '#ffffff', 0.2) : Y < 0.3 ? '#0a090e' : col;
    if (Y > 0.55 && Y < 0.78 && Math.abs(X - Xc) > 0.6) c2 = kind === 'toward' ? '#fff4d8' : kind === 'away' ? '#ff3a30' : '#7a1c1c';
    if (Y > 0.36 && Y < 0.5 && Math.abs(X - Xc) < 0.22) c2 = '#c8c4b8';              // the plate
    put(i, y, c2);
  }
}
// the side street's traffic: cars driving up towards the hills on the right and coming down on the left, a few a loop
// (each a whole number of trips; a car fades into the street's far end), and their lamps' glow
const AV_TRAFFIC = [
  { X: 1.5, n: 5, ph: 0.1, col: '#7a2a24', kind: 'away' }, { X: 1.5, n: 5, ph: 0.55, col: '#5a5e68', kind: 'away' },
  { X: -1.5, n: 4, ph: 0.3, col: '#3a4258', kind: 'toward' }, { X: -1.5, n: 4, ph: 0.8, col: '#6a6458', kind: 'toward' },
];
const avPut = (i, y, col) => { ctx.fillStyle = col; ctx.fillRect(i, y, 1, 1); };
function drawAvenueTraffic(t) {
  for (const c of AV_TRAFFIC) {
    const { u } = cycle(t, c.n, c.ph);
    if (u > 0.62) continue;                                                        // a pause before the next one
    const v = u / 0.62, z = c.kind === 'away' ? lerp(24, 230, v * v) : lerp(230, 22, 1 - (1 - v) * (1 - v));
    avCar(avPut, c.X, z, 4.4, c.col, c.kind);
    const [lx, ly] = proj(c.X, z, 0.66), a = Math.min(1, 60 / z);
    for (const s of [-0.62, 0.62]) halo(c.kind === 'away' ? HL.tail : HL.head, lx + s * F / z, ly, a * (c.kind === 'away' ? 0.8 : 1));
    if (c.kind === 'toward') halo(HL.avBeam, lx, proj(c.X, z - 4, 0)[1], a * 0.7);
  }
}

/* ================= Washingtonia palms swaying in the Santa Ana ================= */
// The very tall, very thin trunk, a small round head of fan fronds and a short skirt of dead ones. The fronds take the
// street's sodium light from below and the moon on their upper tips. The wind comes off the desert from the north-east,
// so every head leans west (left) and swings back in the lulls: the head is prerendered in PALM_NF poses and the trunk
// bends to it per frame.
const FROND = { dk: '#0c0d12', md: '#1a2420', lo: '#3a3a26', up: '#323a52', sk: '#2a1e1c', sk2: '#45302a' };
const PALM_NF = 7, PALM_PAD = 4;
const PALM_Z = [20, 30, 41, 53, 67, 84, 105, 132, 166, 210];
const PALMS = PALM_Z.flatMap((z, k) => [-1, 1].map(side => {
  const ht = 13.7 + 1.1 * Math.sin(k * 2.3 + side), s = F / z, [x, base] = proj(side * 6, z, 0), [, top] = proj(side * 6, z, ht);
  const r = rng(300 + k * 2 + (side > 0 ? 1 : 0));
  return { side, z, k, s, x: Math.round(x), base: Math.round(base), top: Math.round(top), R: Math.max(1.6, 0.98 * s),
    lean: (r() - 0.5) * 0.07, seed: 300 + k * 2 + (side > 0 ? 1 : 0), k1: 14 + ((k * 5 + side * 3 + 40) % 9), k2: 37 + k * 3, ph: r(), sway: Math.max(1, s * 0.22) };
}));
// the sway of one palm at t: -1 (heads blown hard west) .. 0.2 (a lull, nearly upright)
const palmA = (P, t) => clamp(-0.42 + 0.42 * wave(t, P.k1, P.ph) + 0.16 * wave(t, P.k2, P.ph * 1.7) - 0.18 * gustEnv(t), -1, 0.2);
function renderPalmHead(P, a) {
  const R = P.R, S = Math.ceil(R * 2.6) + PALM_PAD * 2, c0 = S >> 1, p = painter(S, S), r = rng(P.seed), DK = FROND.dk, big = P.s > 4;
  if (R < 2.7) {                                                                    // far away: a small spiky tuft
    const T = R < 2.1 ? ['k.k.k', '.kkk.', 'kk.kk'] : ['.k.k.k.', 'k.kkk.k', '.kkkkk.', 'kk.k.kk', '...k...'];
    const off = Math.round(a);
    T.forEach((row, j) => { for (let i = 0; i < row.length; i++) if (row[i] === 'k') p.put(c0 - (row.length >> 1) + i + (j === 0 ? off : 0), c0 - 1 + j, DK); });
    return { c: p.done(), o: c0 };
  }
  if (R > 3.5) for (let y = Math.round(R * 0.15); y < R * 1.0; y++) {               // the skirt of dead fronds hugging the trunk
    const v = y / R, hwd = Math.max(1, Math.round(R * (0.32 - 0.16 * v))), sh = Math.round(-a * v * R * 0.12);
    for (let i = -hwd; i <= hwd; i++) p.put(c0 + i + sh, c0 + y, (i + 7 * y) % 3 === 0 && P.s > 6 ? FROND.sk2 : (i === -hwd ? FROND.sk : '#16100f'));
  }
  const n = R > 8 ? 17 : R > 4.5 ? 13 : 9, fronds = [];
  for (let k = 0; k < n; k++) {
    const a0 = -Math.PI / 2 + ((k + 0.5) / n - 0.5) * 2 * Math.PI * 0.8 + (r() - 0.5) * 0.2;
    fronds.push({ a: a0 + a * 0.22 * Math.cos(a0) * (Math.cos(a0) > 0 ? 1.4 : 0.6) - a * 0.1, L: R * (0.82 + r() * 0.28) * (1 + a * 0.12 * Math.cos(a0)), sp: r() });
  }
  fronds.sort((f, g) => Math.sin(g.a) - Math.sin(f.a));                           // the lower ones first, the upper ones over them
  for (const { a: fa, L, sp: rr } of fronds) {
    const ca = Math.cos(fa), sa = Math.sin(fa), droop = sa < -0.75 ? 0.06 : 0.2 + 0.36 * Math.abs(ca);
    const at = u => [c0 + ca * u * L, c0 + sa * u * L * 0.8 + droop * u * u * L];
    for (let u = 0.1; u <= 0.55; u += 0.5 / L) { const [gx, gy] = at(u); p.put(gx, gy, DK); }   // the stalk
    const [fx, fy] = at(0.45), [tx, ty] = at(1), ta = Math.atan2(ty - fy, tx - fx), tl = Math.hypot(tx - fx, ty - fy);
    const spokes = R > 4.5 ? [-0.5, -0.25, 0, 0.25, 0.5] : [-0.4, 0, 0.4];
    spokes.forEach((o, si) => {                                                    // the fan: stiff segments spreading from the stalk's end
      const len = tl * (1 - 0.35 * Math.abs(o)) * (0.9 + 0.2 * frac(rr * 7 + si * 0.37));
      for (let d = 0; d <= len; d += 0.5) {
        const gx = fx + Math.cos(ta + o) * d, gy = fy + Math.sin(ta + o) * d + 0.2 * d * d / Math.max(2, len), out = d / len;
        let col = out < 0.35 ? DK : FROND.md;
        if (big && out > 0.55 && sa < -0.2 && ca > -0.35) col = FROND.up;          // the moon on the upper tips
        else if (big && sa > 0.15 && out < 0.7) col = FROND.lo;                     // the street's glow under the low fronds
        p.put(gx, gy, col);
      }
    });
  }
  for (let y = -1; y <= 1; y++) for (let i = -1; i <= 1; i++) p.put(c0 + i, c0 + y, DK);   // the crown's heart
  return { c: p.done(), o: c0 };
}
function drawPalm(P, t) {
  const a = palmA(P, t), f = Math.round((a + 1) / 1.2 * (PALM_NF - 1)), H0 = P.base - P.top, top = P.top, tw = P.s >= 11 ? 3 : P.s >= 6.5 ? 2 : 1;
  const dx = a * P.sway;                                                            // the head's offset at the top of the trunk
  for (let y = P.base; y >= top + 1; y--) {
    const v = (P.base - y) / (H0 + 1), xx = Math.round(P.x + P.lean * (P.base - y) + 0.6 * Math.sin(v * 3) + dx * v * v);
    const w = tw + (y > P.base - P.s * 0.7 && P.s > 6 ? 1 : 0);
    ctx.fillStyle = '#0e0c14'; ctx.fillRect(xx - (w >> 1), y, w, 1);
    if (P.s > 4) { ctx.fillStyle = y > P.base - H0 * 0.3 ? '#3a2a30' : '#1e1820'; ctx.fillRect(xx - (w >> 1), y, 1, 1); }
  }
  const hd = PALM_HEADS[P.idx][clamp(f, 0, PALM_NF - 1)], hx = Math.round(P.x + P.lean * H0 + dx);
  ctx.drawImage(hd.c, hx - hd.o, top - hd.o);
}
let PALM_HEADS;
function renderPalmHeads() {
  PALMS.forEach((P, i) => { P.idx = i; });
  return PALMS.map(P => Array.from({ length: PALM_NF }, (_, f) => renderPalmHead(P, -1 + 1.2 * f / (PALM_NF - 1))));
}
function drawPalms(t, near) {                      // near: only the two at the boulevard's corners; otherwise the rest, far to near
  for (let k = PALMS.length - 1; k >= 0; k--) { const P = PALMS[k]; if ((P.z === Z0) === near) drawPalm(P, t); }
}
// the city's warm glow at the foot of the hills, additive
function renderGlow() {
  const p = painter(W, H), col = '#ff9a62';
  for (let y = 60; y < 160; y++) for (let i = 0; i < W; i++) {
    const a = 0.27 * Math.pow(smooth(60, 150, y), 1.4) * (1 - smooth(150, 160, y)) * (0.72 + 0.28 * Math.cos((i - 260) / 260 * Math.PI / 2));
    const q = Math.floor(a * 40 + bayer(i, y)) / 40;
    if (q > 0) p.put(i, y, col, Math.round(q * 255));
  }
  return p.done();
}

/* ================= The Saturn diner: googie roof, red booths, the counter, the pylon sign ================= */
// neon letters, 5 x 7 (the 3 x 5 font's N, M and W are too narrow for a sign)
const NEON5 = {
  S: ['.###.', '#...#', '#....', '.###.', '....#', '#...#', '.###.'], A: ['.###.', '#...#', '#...#', '#####', '#...#', '#...#', '#...#'],
  T: ['#####', '..#..', '..#..', '..#..', '..#..', '..#..', '..#..'], U: ['#...#', '#...#', '#...#', '#...#', '#...#', '#...#', '.###.'],
  R: ['####.', '#...#', '#...#', '####.', '#.#..', '#..#.', '#...#'], N: ['#...#', '##..#', '#.#.#', '#.#.#', '#..##', '#...#', '#...#'],
  D: ['####.', '#...#', '#...#', '#...#', '#...#', '#...#', '####.'], I: ['###', '.#.', '.#.', '.#.', '.#.', '.#.', '###'],
  E: ['#####', '#....', '#....', '####.', '#....', '#....', '#####'],
};
const neonWidth = s => [...s].reduce((a, ch) => a + NEON5[ch][0].length + 1, -1);
// a neon word: the tube's white-hot core, its colour round it on the board
function neonText(p, s, x, y, core, tube) {
  const pts = [];
  let cx = x;
  for (const ch of s) { const g = NEON5[ch]; g.forEach((row, j) => { for (let i = 0; i < row.length; i++) if (row[i] === '#') pts.push([cx + i, y + j]); }); cx += g[0].length + 1; }
  const on = new Set(pts.map(([a, b]) => a + ',' + b));
  for (const [a, b] of pts) for (const [dx, dy] of [[1, 0], [-1, 0], [0, 1], [0, -1]]) if (!on.has((a + dx) + ',' + (b + dy))) p.put(a + dx, b + dy, tube[1]);
  for (const [a, b] of pts) p.put(a, b, (a + b) % 4 === 0 ? tube[0] : core);
}
// a small 3 x 5 word with a wide N
const N4 = ['1001', '1101', '1011', '1001', '1001'];
function smallText(c, s, x, y, col) {
  let cx = x;
  for (const ch of s) {
    if (ch === 'N') { N4.forEach((row, j) => { for (let i = 0; i < 4; i++) if (row[i] === '1') px(c, cx + i, y + j, col); }); cx += 5; continue; }
    text(c, ch, cx, y, col); cx += 4;
  }
}
const D_PANES = [[4, 25], [28, 48], [51, 71], [74, 94]], D_DOOR = [97, 110];   // glass between chrome mullions
const roofY = x => 153 - x * 0.13;                 // the googie roof's front edge sweeps up to the right
// the people inside, per frame (between the lit room and the glass): the couple in the first booth (he eats fries, she
// sips her milkshake), the waitress pouring coffee over the booth's back, a man over his burger, the regular at the
// counter with his cup, the cook's hat moving along the pass. here(rank): whether the crowd level shows them.
const cput = (x, y, c) => { ctx.fillStyle = c; ctx.fillRect(x, y, 1, 1); };
const DINER_CAST = { couple: 0.15, burger: 0.81, regular: 0.38, cook: 0.66 };   // ranks (the waitress is rank 0)
function seated(x, dir, hair, shirt, skin, long, lean = 0, hand = 0) {
  const y0 = 172 + lean;
  for (let j = 0; j < 4; j++) for (let i = 0; i < 4; i++) cput(x + i + (dir > 0 ? lean : -lean), y0 + j, j === 0 || (i === (dir > 0 ? 0 : 3)) ? hair : skin);   // head
  if (long) for (let j = 4; j < 8; j++) cput(x + (dir > 0 ? 0 : 3), 172 + j, hair);
  cput(x + (dir > 0 ? 3 + lean : -lean), y0 + 2, '#2a1a18');                       // the eye side
  for (let j = 0; j < 7; j++) for (let i = 0; i < 5; i++) cput(x - (dir > 0 ? 1 : 0) + i, 176 + j, i === (dir > 0 ? 4 : 0) && j > 2 ? skin : shirt);
  if (hand) { cput(x + (dir > 0 ? 4 : -1), 178, skin); cput(x + (dir > 0 ? 4 : -1), 177, skin); cput(x + (dir > 0 ? 3 : 0), 175, skin); }   // a hand at the mouth
  else for (let i = 0; i < 3; i++) cput(x + (dir > 0 ? 4 + i : -1 - i), 180, skin); // a forearm on the table
}
const WAIT = [
  '...cc...', '..hhhh..', '..hssh..', '..hsss..', '...ss...', '..PwwP..', '.PPwwPP.', '.PPwwPP.', '.PPwwPP.', '.PPwwPP.',
  '.PawwaP.', '.PaaaaP.', '.PaaaaP.', '.PPaaPP.', '.PPPPPP.', '.PPPPPP.', '..s..s..', '..s..s..', '..s..s..', '..s..s..', '.bb..bb.'];
const WAIT_K = { c: '#ffffff', h: '#5a2a1e', s: '#e8b088', P: '#f06a8a', w: '#ffffff', a: '#f4f0ea', k: '#3a2a20', b: '#1a1418' };
const REG = ['..rrrr...', '.rrrrrr..', '..hhhh...', '..hhhh...', '..ssss...', '.PpPpPpP.', 'PpPpPpPpP', 'pPpPpPpPp', 'PpPpPpPps', 'pPpPpPpPs',
  'PpPpPpPp.', '.pPpPpP..', '.jjjjjj..', '.jjjjjjj.'];
const REG_K = { r: '#c83a3a', h: '#3a2418', s: '#a87050', P: '#3a5a8a', p: '#2a3a62', j: '#2a3040' };
function drawWaitress(t) {
  const pour = frac(t / 16 + 0.15), lift = pour < 0.2 ? smooth(0, 0.04, pour) * (1 - smooth(0.16, 0.2, pour)) : 0;
  WAIT.forEach((row, j) => { for (let i = 0; i < row.length; i++) if (WAIT_K[row[i]]) cput(36 + 7 - i, 170 + j, WAIT_K[row[i]]); });
  const py = 176 - Math.round(lift * 3), pxl = 27 - Math.round(lift);
  cput(34, 176 - Math.round(lift * 2), WAIT_K.s); cput(33, py, WAIT_K.s); cput(32, py, '#3a2a20'); cput(31, py, '#3a2a20');   // her arm, the handle
  for (const [x, y, c] of [[pxl, py, '#c8d8e0'], [pxl, py + 1, '#6a3a1e'], [pxl + 1, py + 1, '#5a2e18'], [pxl, py + 2, '#5a2e18'], [pxl + 1, py + 2, '#4a2414']]) cput(x, y, c);
  if (lift > 0.6) for (let y = py + 3; y < 180; y++) cput(pxl - 1, y, '#6a3a1e');   // the coffee pouring
}
function drawDinerLife(t, here) {
  if (here(DINER_CAST.cook)) {                                                     // the cook's hat along the pass
    const hx = 63 + Math.round(3.5 * wave(t, 12, 0.3)), order = frac(t / 30) < 0.25;
    for (let i = 0; i < 4; i++) { cput(hx + i, 165, '#f8f8f4'); cput(hx + i, 166, '#e8e8e4'); }
    cput(hx + 1, 164, '#f8f8f4'); cput(hx + 2, 164, '#f8f8f4'); cput(hx + 1, 167, '#e8b088'); cput(hx + 2, 167, '#e8b088');
    if (order) { cput(69, 167, '#f8f4ec'); cput(70, 167, '#f8f4ec'); cput(69, 166, '#e8a040'); }   // an order up on the pass
  }
  if (here(DINER_CAST.couple)) {
    const fries = frac(t / 12 + 0.3) < 0.1, sip = frac(t / 20 + 0.6) < 0.12;
    seated(7, 1, '#3a2418', '#4a6aa8', '#d8a07a', false, 0, fries);
    seated(18, -1, '#c88a3a', '#e8d8c8', '#e8b490', true, sip ? 1 : 0);
    for (const [x, y, c] of [[15, 177, '#ff9ac8'], [15, 178, '#f070a8'], [16, 177, '#ff9ac8'], [16, 178, '#f070a8'], [15, 176, '#ffe8f0'], [16, 176, '#ffe8f0'], [16, 175, '#d83040']]) cput(x, y, c);
    cput(17 - (sip ? 1 : 0), 174, '#ffffff'); cput(17, 173 + (sip ? 1 : 0), '#ffffff');   // the straw
    for (let i = 11; i <= 13; i++) { cput(i, 181, '#f8f4ec'); cput(i, 180, '#f0c040'); }
    cput(12, 179, '#f6d050');
  }
  // the waitress (rank 0): faces the booth, the pot held out; every 16 s she tops up a cup over the booth's back
  if (here(0)) drawWaitress(t);
  if (here(DINER_CAST.burger)) {
    const bite = frac(t / 15 + 0.45) < 0.1;
    seated(55, 1, '#1a1214', '#6a8a5a', '#a87050', false, 0, bite);
    for (const [x, y, c] of [[61, 179, '#c88a3a'], [62, 179, '#c88a3a'], [61, 180, '#5a2a14'], [62, 180, '#5a2a14'], [61, 181, '#e8a040'], [62, 181, '#e8a040']]) cput(x, y, c);
  }
  if (here(DINER_CAST.regular)) {                                                 // the regular: cap, plaid, his coffee
    const turn = frac(t / 40 + 0.2) < 0.15 ? 1 : 0;
    REG.forEach((row, j) => { for (let i = 0; i < row.length; i++) if (REG_K[row[i]]) cput(79 + i + (j < 5 ? turn : 0), 168 + j, REG_K[row[i]]); });
    const sip = frac(t / 24 + 0.7), up = sip < 0.14 ? smooth(0, 0.03, sip) * (1 - smooth(0.11, 0.14, sip)) : 0;
    const cx = 90 - Math.round(up * 3), cy = 176 - Math.round(up * 4);
    cput(cx, cy, '#f4f4f0'); cput(cx + 1, cy, '#f4f4f0'); cput(cx, cy + 1, '#e0e0dc'); cput(cx + 1, cy + 1, '#e0e0dc');
  } else { cput(90, 176, '#f4f4f0'); cput(91, 176, '#f4f4f0'); cput(90, 177, '#e0e0dc'); cput(91, 177, '#e0e0dc'); }   // his cup waits
}
function renderDinerIn() {
  const p = painter(W, H), { x0, x1, win0, win1 } = DINER;
  for (let y = win0 + 1; y < win1; y++) for (let i = x0; i <= x1; i++) {
    const v = (y - win0) / (win1 - win0);
    let col = v < 0.08 ? '#ffe6b4' : ramp(['#ffe0a8', '#f6cc8e', '#eab878', '#d8a066'], v * 1.4, i, y);
    if (y >= 172 && y <= 175) col = ((i >> 1) + (y >> 1)) & 1 ? '#e8e0d0' : '#2a2a34';      // the checkered tile band
    if (y >= 188) col = ((i >> 1) + y) & 1 ? '#efe6d6' : '#3a3036';                         // the checkered floor
    p.put(i, y, col);
  }
  for (let y = win1; y < BLDG; y++) for (let i = D_DOOR[0]; i <= D_DOOR[1]; i++)   // the floor through the door's lower glass
    p.put(i, y, y === BLDG - 1 ? '#3a3e4a' : ((i >> 1) + y) & 1 ? '#d8cebe' : '#3a3036');
  // the back wall: a menu board, a clock, the kitchen's pass under its heat lamp, shelves of cups, the coffee urn
  for (let y = 164; y <= 169; y++) for (let i = 32; i <= 48; i++) p.put(i, y, y === 164 || y === 169 || i === 32 || i === 48 ? '#8a6a3a' : (y === 166 || y === 168) && (i & 3) !== 3 ? '#e8e4c8' : '#24402e');
  for (const [dx, dy] of [[0, -2], [1, -2], [2, -1], [2, 0], [2, 1], [1, 2], [0, 2], [-1, 2], [-2, 1], [-2, 0], [-2, -1], [-1, -2]]) p.put(55 + dx, 166 + dy, '#3a2a28');
  p.put(55, 166, '#3a2a28'); p.put(55, 165, '#3a2a28'); p.put(56, 166, '#3a2a28');
  for (let i = 60; i <= 72; i++) for (let y = 163; y <= 169; y++) p.put(i, y, y === 163 ? '#c8ccd4' : y === 164 ? '#ff9a4a' : y === 168 ? '#c8ccd4' : y === 169 ? '#8a8e98' : '#5a3424');
  for (let i = 6; i <= 28; i++) { p.put(i, 170, '#c8ccd4'); if (i % 3 === 0) p.put(i, 169, '#f4f4f0'); }
  for (let y = 165; y <= 169; y++) for (let i = 8; i <= 12; i++) p.put(i, y, i === 8 ? '#e8ecf2' : y === 165 ? '#c8ccd4' : '#9aa2b0');
  for (const lx of [15, 38, 61, 104]) { p.put(lx, 161, '#5a4030'); p.put(lx, 162, '#5a4030'); for (let i = -1; i <= 1; i++) p.put(lx + i, 163, '#fff6dc'); p.put(lx, 164, '#ffffff'); }
  for (let i = 30; i <= x1; i++) {                                                  // the counter and its stools
    p.put(i, 178, '#f4f6fa'); p.put(i, 179, '#c22a32'); p.put(i, 180, '#d0d4dc'); p.put(i, 181, '#c22a32'); p.put(i, 182, '#a8222a'); p.put(i, 183, '#6a1a20');
  }
  for (let sx = 33; sx <= x1; sx += 7) {
    p.put(sx, 186, '#e83a3a'); p.put(sx + 1, 186, '#e83a3a'); p.put(sx - 1, 186, '#ff6a6a'); p.put(sx, 187, '#9a2228'); p.put(sx + 1, 187, '#9a2228'); p.put(sx - 1, 187, '#9a2228');
    for (let y = 188; y < win1; y++) p.put(sx, y, '#b8bec8');
  }
  for (let i = 66; i <= 72; i++) for (let y = 174; y <= 177; y++) p.put(i, y, y === 174 ? '#e8f0f4' : i === 66 || i === 72 ? '#c8d8e0' : y === 176 ? (i & 1 ? '#e8b048' : '#d86a4a') : '#f4ece0');
  for (const bx of [4, 51]) {                                                      // booths: red tufted backs end-on, a table between
    for (const ex of [bx, bx + 18]) for (let y = 175; y < win1; y++) for (let i = 0; i < 3; i++)
      p.put(ex + i, y, y === 175 ? '#e8ecf2' : i === 0 ? '#ff5a5a' : (y & 1) ? '#c8303a' : '#b02830');
    for (let i = bx + 4; i <= bx + 16; i++) { p.put(i, 182, '#f4f0ea'); p.put(i, 183, '#9aa2b0'); }
    p.put(bx + 10, 184, '#9aa2b0'); for (let y = 185; y < win1; y++) p.put(bx + 10, y, '#7a828e');
  }
  return p.done();
}
function renderDinerOut() {
  const p = painter(W, H), { x0, x1, wall, win0, win1 } = DINER;
  for (let y = 161; y <= 167; y++) for (let i = 77; i <= 94; i++) p.put(i, y, y === 161 || y === 167 || i === 77 || i === 94 ? '#5a3a3a' : '#2a1420');   // the OPEN sign's board
  const CHR = ['#f2f4f8', '#b8bec8', '#6a7080'];
  for (let i = x0; i <= x1 + 1; i++) { p.put(i, win0, '#d8dce4'); p.put(i, win1, '#e8ecf2'); }
  for (const [a] of [...D_PANES, D_DOOR]) for (let y = win0; y <= win1; y++) if (a > x0) { p.put(a - 2, y, CHR[0]); p.put(a - 1, y, CHR[2]); }
  for (let y = win0; y < BLDG; y++) { p.put(x1 + 1, y, CHR[0]); p.put(x1 + 2, y, CHR[2]); }
  for (let y = win1 + 1; y < BLDG; y++) for (let i = x0; i <= x1; i++) {            // the stainless knee wall, a red stripe on top
    const k = y - win1 - 1;
    if (i >= D_DOOR[0] && i <= D_DOOR[1]) continue;                               // (the door's lower glass shows the floor)
    p.put(i, y, k === 0 ? '#d8343c' : k === 1 ? '#f0f2f6' : y === BLDG - 1 ? '#3a3e4a' : (k & 1) ? '#c4cad6' : '#8e96a6');
  }
  for (let y = win0 - 2; y < BLDG; y++) for (let i = x1 + 3; i <= wall; i++) {      // the stacked-stone wall, pink under the sign
    const row = Math.floor((y - win0) / 3), off = (row * 5) % 7, blk = Math.floor((i + off) / 7);
    const joint = (y - win0 + 30) % 3 === 2 || (i + off) % 7 === 0;
    const pink = clamp(1 - Math.hypot((i - 121) / 22, (y - 168) / 30), 0, 1);
    let col = joint ? '#241c24' : ['#5e5056', '#6c5c5e', '#54484e', '#64545a'][Math.floor(hash(blk, row, 31) * 4)];
    if (!joint && pink > bayer(i, y) * 0.9) col = tint(col, '#ff6aa8', 0.28 * pink + 0.08);
    if (i === wall) col = '#1a1420';
    p.put(i, y, col);
  }
  for (let i = x0 - 1; i <= wall + 4; i++) {                                       // the googie roof and its lit soffit
    const ry = Math.round(roofY(i));
    p.put(i, ry, '#f4f6f8'); p.put(i, ry + 1, '#3ab8b0'); p.put(i, ry + 2, '#228a88'); p.put(i, ry + 3, '#14585c');
    for (let y = ry + 4; y < win0 - (i > x1 + 2 ? 2 : 0); y++) {
      const v = (y - ry - 4) / Math.max(1, win0 - ry - 4);
      p.put(i, y, ramp(['#4a2e2a', '#6a4232', '#8a5a3e', '#a8744a'], 0.25 + v * 0.8, i, y));
    }
    if (i % 9 === 4 && i < wall) p.put(i, ry + 4, '#fff4d0');
  }
  const POT = ['...g.o..', '..gg.oo.', '.g.ggo..', 'g..gg.g.', '...gg..g', '...gg...', '..tttt..', '..tTtt..', '..tttt..', '...tt...'];
  POT.forEach((row, j) => { for (let i = 0; i < row.length; i++) {
    const col = { g: '#2e6a46', o: '#ff8a3a', t: '#b0603a', T: '#d88a5a' }[row[i]];
    if (col) p.put(110 + i, 190 + j, col);
  } });
  for (let y = 141; y <= 146; y++) for (let i = 28; i <= 31; i++) p.put(i, y, y === 141 ? '#e8ecf2' : i === 28 ? '#c8ccd4' : '#7a808c');   // the kitchen's vent
  for (let i = 27; i <= 32; i++) p.put(i, 140, '#9aa0ac');
  const c = p.done();
  smallText(p.x, 'OPEN', 78, 162, '#ff6a9a');
  return c;
}
// the door: shut (its push bar) or swung open on its hinge as someone goes in or out
const DOOR_OPEN = [[49, 53], [226, 230]];
function drawDoor(t) {
  if (inEvent(t, DOOR_OPEN)) { for (let y = DINER.win0 + 1; y < BLDG; y++) { cput(D_DOOR[1], y, '#e8ecf2'); cput(D_DOOR[1] - 1, y, '#8a92a0'); } }
  else for (let i = D_DOOR[0] + 1; i < D_DOOR[1]; i++) cput(i, 180, '#e8ecf2');
}
// the pylon: a white steel blade leaning right, Saturn and its ring on top, the name board, a starburst
const SAT = { x: 124, y: 86, r: 6.5 };
const ringPt = (deg, rr = 13) => {
  const t = deg * Math.PI / 180, ex = Math.cos(t) * rr, ey = Math.sin(t) * rr * 3.4 / 13;
  return [SAT.x + ex * Math.cos(-0.24) - ey * Math.sin(-0.24), SAT.y + ex * Math.sin(-0.24) + ey * Math.cos(-0.24), Math.sin(t) > 0];
};
function renderPylon(off) {
  const p = painter(W, H);
  if (!off) {
    for (let y = 98; y < BLDG; y++) {                                          // the blade, tapering and leaning
      const u = (y - 98) / (BLDG - 98), w = Math.round(lerp(4, 6, u)), xl = Math.round(lerp(122, 118, u));
      for (let k = 0; k < w; k++) p.put(xl + k, y, k === 0 ? '#f4f6f8' : k === w - 1 ? '#3a4050' : k === 1 ? '#3ab8b0' : '#228a88');
    }
    for (let i = 116; i <= 127; i++) { p.put(i, BLDG - 2, '#5a5e6c'); p.put(i, BLDG - 1, '#3a3e4a'); }
    const ring = front => { for (let a = 0; a < 360; a += 1.5) { const [rx, ry, f] = ringPt(a); if (f !== front) continue; p.put(rx, ry, '#d8fff8'); p.put(rx, ry + 1, '#3ad8c8'); } };
    for (let y = SAT.y; y < 99; y++) p.put(122, y, '#3a4050');                 // the mast from the board up to the planet
    ring(false);
    for (let y = -7; y <= 7; y++) for (let i = -7; i <= 7; i++) {
      const d = Math.hypot(i, y);
      if (d > SAT.r) continue;
      const band = Math.floor((y + 7) / 2.4) & 1, shade = i * 0.6 + y * 0.3 > 2.4;
      p.put(SAT.x + i, SAT.y + y, d > SAT.r - 1 && i > 0 ? '#a84a24' : shade ? (band ? '#c8622c' : '#b85a28') : band ? '#ffc65a' : '#ff9e44');
    }
    ring(true);
    for (const [dx, dy, c] of [[0, 0, '#ffffff'], [0, -1, '#fff4b0'], [0, 1, '#fff4b0'], [-1, 0, '#fff4b0'], [1, 0, '#fff4b0'], [0, -2, '#ffd86a'], [0, 2, '#ffd86a'], [-2, 0, '#ffd86a'], [2, 0, '#ffd86a'], [0, -3, '#c89a3a'], [3, 0, '#c89a3a'], [-3, 0, '#c89a3a']])
      p.put(110 + dx, 74 + dy, c);                                              // a googie starburst over Saturn
  }
  for (let y = 99; y <= 113; y++) for (let i = 76 + Math.round((113 - y) * 0.3); i <= 129; i++) {   // the name board
    const edge = y === 99 || y === 113 || i === 129 || i === 76 + Math.round((113 - y) * 0.3);
    p.put(i, y, edge ? (y === 99 ? '#f4f6f8' : '#9aa2b0') : '#180f30');
  }
  neonText(p, 'SATURN', 86, 102, off ? '#6a3050' : '#ffd8ec', off ? ['#4a2040', '#241228'] : ['#ff5aa6', '#7a1c52']);
  if (off) return p.done();
  for (let y = 115; y <= 125; y++) for (let i = 94; i <= 127; i++) p.put(i, y, y === 115 || y === 125 || i === 94 || i === 127 ? '#2a9a98' : '#14102a');
  neonText(p, 'DINER', 97, 117, '#d8fff8', ['#3ad8c8', '#14504c']);
  return p.done();
}
const SAT_BUZZ = [[33, 34.6], [141, 142.4], [206.5, 208.2]];
function satLevel(t) {
  if (!inEvent(t, SAT_BUZZ)) return 0.94 + 0.06 * wave(t, 2400);
  const hv = hash(9, Math.floor(t * 24)); return hv > 0.5 ? 1 : hv > 0.25 ? 0.35 : 0;
}
// a bright run of light orbiting the ring (30 turns a loop), the starburst twinkling
function drawPylonLife(t, m) {
  if (m < 0.5) ctx.drawImage(PYLON_OFF, 0, 0);
  const ph = frac(t / 8) * 360;
  for (let k = 0; k < 7; k++) {
    const [rx, ry, front] = ringPt(ph - k * 6);
    if (!front && Math.hypot(rx - SAT.x, ry - SAT.y) < SAT.r + 0.5) continue;      // behind the planet
    cput(Math.round(rx), Math.round(ry), k < 2 ? '#ffffff' : '#a8fff4');
  }
  const tw = frac(t / 3) < 0.5 ? 1 : 0;
  if (tw) for (const [dx, dy] of [[0, -4], [4, 0], [-4, 0], [0, 4]]) cput(110 + dx, 74 + dy, '#8a6a2a');
  halo(HL.star, 110, 74, 0.7 + 0.3 * tw);
}

/* ================= The record shop on the right corner, a cat in its window ================= */
function renderShop() {
  const p = painter(W, H), { x0, x1, top } = SHOP;
  for (let y = top; y < BLDG; y++) for (let i = x0; i <= x1; i++) {               // dusty-rose stucco, warmer low down
    let col = ramp(['#3a2a3a', '#46303e', '#523844', '#5e404a'], 0.25 + 0.6 * (y - top) / (BLDG - top) - (i < x0 + 2 ? -0.4 : 0), i, y);
    if (y === top) col = '#a8949c'; else if (y === top + 1) col = '#6a5662'; else if (y === top + 2) col = '#2a1e2a';
    p.put(i, y, col);
  }
  // an awning over the window and door: dark green canvas, scalloped
  for (let i = x0 + 6; i <= x1; i++) for (let y = 158; y <= 163; y++) {
    if (y === 163 && (i % 4 === 0)) continue;
    p.put(i, y, y === 158 ? '#3a7a62' : ((i >> 2) & 1) ? '#1e4a3a' : '#245a46');
  }
  // the shop window: bins of records, sleeves on the wall, the RECORDS neon
  const WN = { x0: 368, x1: 414, y0: 166, y1: 192 };
  for (let y = WN.y0; y <= WN.y1; y++) for (let i = WN.x0; i <= WN.x1; i++) {
    const edge = y === WN.y0 || y === WN.y1 || i === WN.x0 || i === WN.x1;
    p.put(i, y, edge ? '#140e16' : ramp(['#d4a46a', '#c08e5a', '#a87a4c', '#8a6040'], (y - WN.y0) / (WN.y1 - WN.y0) * 1.2, i, y));
  }
  const SLV = ['#d84a3a', '#3a6ab8', '#e8c040', '#2a2a30', '#e87ab0', '#4a9a6a', '#f0e8d8', '#8a4ac8'];
  for (let i = WN.x0 + 3; i < WN.x1 - 3; i += 6) for (let y = 175; y <= 179; y++) for (let k = 0; k < 5; k++) p.put(i + k, y, SLV[(i * 7 + 3) % 8]);
  for (let i = WN.x0 + 2; i < WN.x1 - 1; i++) {                                    // the bins: sleeves on edge
    for (let y = 185; y <= 191; y++) p.put(i, y, y === 185 ? SLV[(i >> 1) % 8] : y < 188 ? '#5a3a2a' : '#3a2620');
    if (hash(i, 3, 15) < 0.6) p.put(i, 184, SLV[(i * 3) % 8]);
  }
  for (let i = WN.x0 + 1; i < WN.x1; i++) p.put(i, 183, '#6a4430');
  // the door, its lit transom; the dark second window with the shop cat on the sill
  for (let y = 164; y < BLDG; y++) for (let i = 419; i <= 431; i++) p.put(i, y, i === 419 || i === 431 ? '#140e16' : y < 168 ? '#e8b878' : y === 168 ? '#140e16' : '#221a26');
  p.put(429, 184, '#c8a060');
  for (let y = 166; y <= 192; y++) for (let i = 437; i <= 476; i++) {
    const edge = y === 166 || y === 192 || i === 437 || i === 476;
    p.put(i, y, edge ? '#140e16' : ramp(['#3a2a40', '#30223a', '#281c32'], (y - 166) / 26, i, y));
  }
  for (let i = 437; i <= 476; i++) p.put(i, 192, '#4a3a44');
  for (let y = 193; y < BLDG; y++) for (let i = x0; i <= x1; i++) p.put(i, y, y === BLDG - 1 ? '#1a1218' : '#2e2028');
  const c = p.done();
  text(p.x, 'RECORDS', 378, 168, '#7ad8ff');
  return c;
}

/* ================= The billboard on the shop's roof ================= */
// A rooftop billboard on a steel frame: two legs and a catwalk on the shop's parapet, three gooseneck lamps over the top
// edge. The city's own ad is a film's: NIGHT DRIVE, a convertible and a palm against a big setting sun, opening
// Friday (its premiere is the searchlights' night).
const BB_X0 = BB.x - 2, BB_X1 = BB.x + BB.w + 1, BB_Y0 = BB.y - 2, BB_Y1 = BB.y + BB.h + 1;
const BB_LAMPS = [0.2, 0.5, 0.8].map(f => BB.x + Math.round((BB.w - 1) * f)), BB_LENS = BB_Y0 - 4;
function renderBillboard() {
  const p = painter(W, H), DK = '#0e0c16', MD = '#1e1a2a', LT = '#3c3650';
  for (const lx of [BB.x + 8, BB.x + BB.w - 10]) for (let y = BB_Y1 + 2; y <= SHOP.top; y++) { p.put(lx, y, LT); p.put(lx + 1, y, DK); }
  for (let i = BB.x + 9; i < BB.x + BB.w - 10; i++) { const u = (i - BB.x - 9) / (BB.w - 19); p.put(i, Math.round(BB_Y1 + 3 + u * 6), MD); p.put(i, Math.round(SHOP.top - 1 - u * 6), MD); }
  for (let i = BB_X0 - 3; i <= BB_X1 + 3; i++) { p.put(i, BB_Y1 + 1, (i & 1) ? MD : LT); p.put(i, BB_Y1 + 2, DK); }   // the catwalk
  for (const ex of [BB_X0 - 3, BB_X1 + 3]) for (let y = BB_Y1 - 3; y <= BB_Y1; y++) p.put(ex, y, LT);
  for (let i = BB_X0 - 3; i <= BB_X1 + 3; i++) p.put(i, BB_Y1 - 3, MD);
  for (let i = BB_X0; i <= BB_X1; i++) { p.put(i, BB_Y0, '#4a4460'); p.put(i, BB_Y1, DK); }
  for (let y = BB_Y0; y <= BB_Y1; y++) { p.put(BB_X0, y, '#3a3450'); p.put(BB_X1, y, DK); }
  for (let i = BB_X0 + 1; i < BB_X1; i++) { p.put(i, BB_Y0 + 1, '#26203a'); p.put(i, BB_Y1 - 1, '#5a4438'); }
  for (let y = BB_Y0 + 1; y < BB_Y1; y++) { p.put(BB_X0 + 1, y, '#26203a'); p.put(BB_X1 - 1, y, '#16121e'); }
  for (const lx of BB_LAMPS) {
    for (let y = BB_LENS + 1; y < BB_Y0; y++) p.put(lx, y, LT);
    for (let i = lx - 2; i <= lx + 2; i++) p.put(i, BB_LENS - 1, i === lx - 2 ? LT : i === lx + 2 ? DK : MD);
    for (let i = lx - 1; i <= lx + 1; i++) p.put(i, BB_LENS - 2, MD);
  }
  return p.done();
}
const BB_NIGHT = { level: 0.8, tint: '#231a44', mix: 0.12, wear: 0.25, seed: 41 };
function renderBillboardAd() {
  const { w, h } = BB, p = painter(w, h);
  for (let y = 0; y < h; y++) for (let i = 0; i < w; i++) p.put(i, y, ramp(['#1a1238', '#2a1648', '#46185a', '#6a1c62', '#962a5e'], (y / (h - 1)) * 0.9 + (i < 20 ? 0.15 : -0.1), i, y));
  for (let y = 0; y < h; y++) for (let i = 0; i < 20; i++) {                     // the setting sun, banded
    const d = Math.hypot(i - 10, y - 12);
    if (d < 8 && y < 15) p.put(i, y, y > 10 && (y & 1) ? '#c83a6a' : ramp(['#ff6a5a', '#ff9a4a', '#ffd06a'], 1 - d / 8 + (11 - y) * 0.04, i, y));
  }
  for (let i = 0; i < 22; i++) { p.put(i, 15, '#120c22'); p.put(i, 16, '#1e1430'); }  // the road
  for (let i = 0; i < 22; i++) for (let y = 17; y < h; y++) p.put(i, y, '#120c22');
  const CAR = ['....kkkk......', '..kkkkkkkkkk..', 'kkkkkkkkkkkkkk', '.kk.......kk..'];
  CAR.forEach((row, j) => { for (let i = 0; i < row.length; i++) if (row[i] === 'k') p.put(4 + i, 11 + j, '#0c0818'); });
  p.put(4, 13, '#ffe0a0'); p.put(17, 13, '#ff3a3a');
  for (let y = 3; y < 15; y++) p.put(19 + (y < 6 ? 1 : 0), y, '#0c0818');      // a palm's trunk and head
  for (const [dx, dy] of [[-3, 0], [-2, -1], [-1, -1], [0, -1], [1, -1], [2, -1], [3, 0], [-2, 1], [2, 1], [4, 1], [-4, 1], [0, -2], [1, 0], [-1, 0]]) p.put(20 + dx, 3 + dy, '#0c0818');
  const c = p.done();
  smallText(p.x, 'NIGHT', 24, 2, '#fff2e0'); text(p.x, 'DRIVE', 25, 8, '#ffb85a');
  text(p.x, 'FRIDAY', 23, 14, '#c8a0d8');
  return gradeAd(c, BB_NIGHT);
}

/* ================= The red convertible at the curb ================= */
// a late-fifties convertible, top down, facing west: tail fins and bullet lamps, whitewalls, a chrome spear
function renderCar() {
  const w = 60, h = 18, p = painter(w, h);
  const beltY = i => i > 48 ? 7 - Math.min(3, Math.round((i - 48) * 0.5)) : 7;   // the fin rises at the rear
  for (let i = 1; i < w - 2; i++) {
    const top = beltY(i), nose = i < 4 ? 9 : 8;
    for (let y = Math.max(top, i < 4 ? nose : top); y <= 14; y++) {
      let col = y === top ? '#ff7a6a' : y === top + 1 ? '#e8343a' : y < 12 ? '#c8202a' : '#8a141c';
      if (y === 10 && i > 5 && i < 50) col = '#eef2f6';                       // the chrome side spear
      p.put(i, y, col);
    }
  }
  for (let i = 22; i <= 46; i++) for (let y = 4; y < 7; y++) if (y > 4 || (i > 24 && i < 30) || (i > 36 && i < 42)) p.put(i, y, y === 4 ? '#f0e4cc' : '#c8b494');   // seats, headrests
  for (let k = 0; k < 6; k++) { p.put(18 + Math.round(k * 0.7), 6 - k, '#c8ccd4'); if (k > 0) p.put(19 + Math.round(k * 0.7), 6 - k, '#8ac8e0'); }   // the windscreen
  for (let i = 18; i <= 24; i++) p.put(i, 1, '#d8dce4');
  for (let y = 9; y <= 12; y++) { p.put(0, y, '#e8ecf2'); p.put(1, y, '#b8bec8'); p.put(w - 2, y, '#e8ecf2'); p.put(w - 3, y, '#b8bec8'); }   // bumpers
  p.put(1, 8, '#fff6d8'); p.put(2, 8, '#fff0c0'); p.put(w - 3, 5, '#ff4a3a'); p.put(w - 3, 6, '#ff2a2a');
  const WHEEL = ['.kkkkk.', 'kwwwwwk', 'kwhHhwk', 'kwHHHwk', 'kwhHhwk', 'kwwwwwk', '.kkkkk.'];
  const WK = { k: '#121016', w: '#d8d4cc', h: '#7a808c', H: '#dfe3ea' };
  for (const wx of [13, 46]) {
    for (let i = wx - 5; i <= wx + 5; i++) for (let y = 9; y <= 11; y++) if (Math.hypot(i - wx, y - 14) < 5.6) p.put(i, y, '#3a0c12');   // the wheel arch
    WHEEL.forEach((row, j) => { for (let i = 0; i < 7; i++) if (WK[row[i]] && 10 + j >= 11) p.put(wx - 3 + i, 10 + j, WK[row[i]]); });
  }
  return p.done();
}

/* ================= The bus stop in front of the shop ================= */
const STOP = { x: 434, bench: [440, 470] };
function renderStop() {
  const p = painter(W, H), [b0, b1] = STOP.bench;
  for (let i = b0; i <= b1; i++) { p.put(i, 194, '#2a6a5a'); p.put(i, 195, '#1e5044'); p.put(i, 197, '#2a6a5a'); p.put(i, 198, '#1a4a3e'); }
  for (const lx of [b0 + 2, b1 - 2]) for (let y = 194; y <= 204; y++) p.put(lx, y, '#1a1a22');
  for (let y = 172; y <= 205; y++) p.put(STOP.x, y, y > 202 ? '#4a4656' : '#3a3a44');             // the sign's pole
  for (let y = 172; y <= 178; y++) for (let i = STOP.x - 3; i <= STOP.x + 3; i++) p.put(i, y, y === 172 ? '#f0f0f0' : (i + y) % 5 === 0 ? '#a87818' : '#d8a020');
  p.put(STOP.x, 175, '#2a2a30'); p.put(STOP.x - 1, 175, '#2a2a30'); p.put(STOP.x + 1, 175, '#2a2a30');
  return p.done();
}

/* ================= The north sidewalk, the boulevard, the south kerb ================= */
const LAMP = { x: 352, top: 140 };
const SOUTH = 252;                                  // the south kerb (Z 11.45): the boulevard's two lanes lie between the kerbs
// the neon's and the lamps' light lying on the asphalt: [cx, cy, rx, ry, colour, strength]
const SHINE = [[120, 216, 34, 10, '#ff5aa0', 0.55], [52, 214, 66, 8, '#ffb860', 0.4], [124, 230, 18, 14, '#ff8a4a', 0.25],
  [LAMP.x, 214, 30, 7, '#ffc070', 0.5], [392, 213, 28, 5, '#ffb060', 0.35], [256, 211, 44, 4, '#ffcf88', 0.22], [460, 213, 20, 4, '#9ad0ff', 0.2],
  [110, 262, 130, 18, '#ffb070', 0.42], [400, 262, 120, 16, '#ffc890', 0.38]];   // the south side's lamps, behind us
function shine(i, y, col) {
  for (const [cx, cy, rx, ry, lc, st] of SHINE) {
    const d = Math.hypot((i - cx) / rx, (y - cy) / ry);
    if (d >= 1) continue;
    const q = Math.floor((1 - d) * st * 5 + bayer(i, y)) / 5;
    if (q > 0) col = tint(col, lc, Math.min(0.55, q * 0.7));
  }
  return col;
}
// a Walk of Fame star seen low: a charcoal square, the coral star with its brass rim, a brass emblem in the middle
function walkStar(p, cx, y0, w, h) {
  for (let y = 0; y < h; y++) for (let i = 0; i < w; i++) {
    const u = (i + 0.5) / w * 2 - 1, v = (y + 0.5) / h * 2 - 1, r = Math.hypot(u, v * 1.05), a = Math.atan2(v, u);
    const k = frac((a + Math.PI / 2) / (TAU / 5) + 10), star = 0.95 - 0.55 * Math.abs(k - Math.round(k)) / 0.5;
    let col = (i === 0 || y === 0) ? '#3a3440' : '#26222c';
    if (r < star) col = r > star - 0.16 ? '#c8a050' : '#e8909c';
    if (r < 0.2) col = '#d8b060';
    p.put(cx - (w >> 1) + i, y0 + y, col);
  }
}
function renderStreet() {
  const p = painter(W, H);
  // the north sidewalk, lit by the diner's windows; the Walk of Fame's stars set into it
  for (let y = BLDG; y < CURB; y++) for (let i = 0; i < W; i++) {
    const Z = F * EYE / (y + 0.5 - HZN), X = (i + 0.5 - VPX) * Z / F;
    if (Math.abs(X) < 4.25 && y > BLDG + 1) continue;                            // the side street's mouth (drawn with it)
    let v = 0.35 + 0.15 * (y - BLDG) / 8;
    if (i < DINER.x1 + 6) v += 0.35 * (1 - Math.abs(((i - 4) % 23) - 11) / 14);
    let col = ramp(['#221c28', '#2c2532', '#3a2f3a', '#4c3c42', '#5e4a48'], v, i, y);
    if ((i + 6) % 17 === 0) col = '#1c1822';
    p.put(i, y, col);
  }
  for (let sx = 8; sx < W; sx += 34) {
    const Z = F * EYE / (203.5 - HZN), X = (sx - VPX) * Z / F;
    if (Math.abs(X) < 5) continue;
    for (let y = 202; y <= 205; y++) for (let i = sx - 4; i <= sx + 4; i++) p.put(i, y, '#2a2630');
    for (const [dx, dy, c] of [[0, 202, '#e8a0b0'], [-1, 203, '#e8a0b0'], [0, 203, '#f4c0c8'], [1, 203, '#e8a0b0'], [-2, 203, '#c88090'], [2, 203, '#c88090'], [-1, 204, '#d890a0'], [1, 204, '#d890a0']]) p.put(sx + dx, dy, c);
    p.put(sx - 4, 202, '#b8984a'); p.put(sx + 4, 205, '#b8984a');
  }
  // the north kerb, opening at the side street
  for (let i = 0; i < W; i++) {
    const X = (i + 0.5 - VPX) * Z0 / F;
    if (Math.abs(X) < 4) continue;
    p.put(i, CURB, '#8a7e86'); p.put(i, CURB + 1, '#2a2430');
  }
  // the boulevard: asphalt, the parking lane's line, the double yellow; crosswalks at both corners
  for (let y = CURB + 1; y < SOUTH; y++) {
    const Z = F * EYE / (y + 0.5 - HZN);
    for (let i = 0; i < W; i++) {
      if (y === CURB + 1 && Math.abs((i + 0.5 - VPX) * Z0 / F) >= 4) continue;
      const X = (i + 0.5 - VPX) * Z / F;
      let col = ramp(['#0e0c14', '#13111a', '#18151f', '#1e1a25', '#262029'], 0.3 + 0.25 * hash(i >> 1, y, 5) + 0.25 * (1 - (y - CURB) / 50), i, y);
      if (Math.abs(Z - 17.6) < 0.09 * Z / 10) col = '#5a5460';
      if (y === 230 || y === 232) col = '#b8963a';
      const aX = Math.abs(X);
      if (aX > 4.6 && aX < 8.4 && Z > 11.7 && Z < 19.7 && frac((19.7 - Z) / 1.2) < 0.5) col = hash(i >> 1, y, 9) < 0.22 ? '#4e4856' : '#8e8792';
      p.put(i, y, shine(i, y, col));
    }
  }
  for (let y = 238; y <= 241; y++) for (let i = 196; i <= 214; i++) {                 // a manhole cover
    const d = Math.hypot((i - 205) / 9.5, (y - 239.5) / 2.2);
    if (d < 1) p.put(i, y, d > 0.8 ? '#2e2a34' : ((i + y) & 1) ? '#1c1a22' : '#24212a');
  }
  for (const [x0, y0, w, h] of [[300, 243, 26, 4], [60, 222, 18, 3], [404, 225, 14, 3]])   // patched asphalt
    for (let y = y0; y < y0 + h; y++) for (let i = x0; i < x0 + w; i++) if (hash(i >> 2, y, 12) < 0.9) p.put(i, y, shine(i, y, '#0c0b12'));
  for (let i = 150; i <= 166; i++) { p.put(i, CURB + 1, '#08070c'); if (i & 1) p.put(i, CURB, '#3a3440'); }   // a storm drain
  // the south kerb and our own sidewalk, its stars big this close
  for (let i = 0; i < W; i++) { p.put(i, SOUTH, shine(i, SOUTH, '#8a7e86')); p.put(i, SOUTH + 1, shine(i, SOUTH + 1, '#5a5058')); }
  for (let y = SOUTH + 2; y < H; y++) for (let i = 0; i < W; i++) {
    const Z = F * EYE / (y + 0.5 - HZN), X = (i + 0.5 - VPX) * Z / F;
    let col = ramp(['#2a2430', '#342c38', '#3e3440', '#4a3e46'], 0.45 + 0.2 * hash(i >> 2, y >> 1, 21), i, y);
    if (frac(X / 3.2) < 0.03 * Z / 6) col = '#1e1a24';                              // the slabs' joints
    p.put(i, y, shine(i, y, col));
  }
  for (const sx of [48, 152, 256, 360, 464]) walkStar(p, sx, SOUTH + 4, 26, 9);
  // a fire hydrant on our kerb, LA yellow
  const HY = ['..ccc..', '.ccccc.', '..yyy..', 'oyyyyyo', 'oyYyyyo', '.yYyyy.', '.yYyyy.', '.yyyyy.', 'kkkkkkk'];
  HY.forEach((row, j) => { for (let i = 0; i < row.length; i++) { const col = { c: '#c8ccd4', y: '#d8a828', Y: '#ffd860', o: '#b88a20', k: '#3a3020' }[row[i]]; if (col) p.put(98 + i, 254 + j, col); } });
  // the lamp at the right corner: a twin-lantern post
  const { x: lx, top } = LAMP;
  for (let y = top + 4; y <= 205; y++) { const w = y > 196 ? 3 : 1; for (let k = 0; k < w; k++) p.put(lx - (w >> 1) + k, y, k === 0 && w > 1 ? '#4a4656' : '#16141e'); }
  for (let i = lx - 6; i <= lx + 6; i++) p.put(i, top + 5, '#16141e');
  for (const gx of [lx - 6, lx + 6]) { for (let y = top; y <= top + 4; y++) for (let k = -1; k <= 1; k++) p.put(gx + k, y, y === top ? '#16141e' : '#fff0c8'); p.put(gx, top - 1, '#16141e'); }
  p.put(lx, top + 2, '#16141e'); p.put(lx, top + 3, '#16141e'); p.put(lx, top + 4, '#16141e');
  return p.done();
}
function renderVignette() {
  const [c, x] = makeCanvas(W, H), img = x.createImageData(W, H), d = img.data;
  for (let y = 0; y < H; y++) for (let i = 0; i < W; i++) {
    const v = smooth(0.78, 1.45, Math.hypot((i - W / 2) / (W / 2), (y - H / 2) / (H / 2) * 0.9));
    const q = Math.floor(v * 4 + bayer(i, y)) / 4;
    if (q <= 0) continue;
    const o = (y * W + i) * 4; d[o] = 6; d[o + 1] = 4; d[o + 2] = 16; d[o + 3] = Math.round(q * 0.42 * 255);
  }
  x.putImageData(img, 0, 0);
  return c;
}

/* ================= The Santa Ana: three gusts a loop ================= */
// Each gust is a displacement that rises over its window and is paid back slowly over the whole loop, so
// gustX(0) = gustX(240) = 0 (as New York's): anything airborne is pushed by gustX(now) - gustX(birth).
const GUSTS = [[44, 52, -150], [118, 126, -170], [196, 204, -150]];
function gustX(t) {
  const tt = ((t % LOOP) + LOOP) % LOOP;
  let g = 0;
  for (const [a, b, D] of GUSTS) g += D * (smooth(a, b, tt) - tt / LOOP);
  return g;
}
const gustEnv = t => GUSTS.reduce((m, [a, b]) => Math.max(m, env(t, a - 1, b + 2, 3)), 0);

/* ================= Big moment 1: searchlights for the premiere ================= */
// NIGHT DRIVE premieres down the boulevard to the west: two searchlights behind the Franklin blocks sweep the sky in
// slow counter-phase, one lit after the other, for about fifty seconds; they never cross the sign.
const SEARCH = { t0: 18, t1: 70, x: 96, y: 140, per: 13 };
function renderSearchBeam() {
  const L = 330, Wd = 36, p = painter(Wd, L);
  for (let r = 0; r < L; r++) {
    const hw = 0.9 + r * 0.06, a0 = 0.34 * Math.pow(1 - r / L, 0.7) * smooth(0, 14, r);
    for (let i = 0; i < Wd; i++) {
      const dx = Math.abs(i + 0.5 - Wd / 2) / hw;
      if (dx >= 1) continue;
      const q = Math.floor(a0 * (1 - dx * dx) * 24 + bayer(i, r)) / 24;
      if (q > 0) p.put(i, L - 1 - r, '#dce6ff', Math.round(Math.min(1, q) * 255));
    }
  }
  return p.done();
}
function drawSearchlights(t) {
  if (t < SEARCH.t0 || t >= SEARCH.t1) return;
  const lv = [smooth(SEARCH.t0, SEARCH.t0 + 5, t) * (1 - smooth(SEARCH.t1 - 6, SEARCH.t1, t)),
    smooth(SEARCH.t0 + 5, SEARCH.t0 + 10, t) * (1 - smooth(SEARCH.t1 - 9, SEARCH.t1 - 3, t))];
  lv.forEach((a, k) => {
    if (a < 0.01) return;
    const th = 0.62 * Math.sin(TAU * (t - SEARCH.t0) / SEARCH.per + k * 2.4);
    ctx.save();
    ctx.translate(SEARCH.x + k * 5, SEARCH.y); ctx.rotate(th);
    ctx.globalCompositeOperation = 'lighter'; ctx.globalAlpha = a;
    ctx.drawImage(SBEAM_C, -(SBEAM_C.width >> 1), -SBEAM_C.height);
    ctx.restore();
  });
}

/* ================= Big moment 2: the observatory opens its east dome to the moon ================= */
// The public's telescope night: a few visitors walk out onto the terrace and queue by the east dome, which turns
// a little towards the moon, opens its shutter, shows the telescope's tube and closes again.
const DOME = { q0: 90, t0: 100, open: 106, tele: 112, close: 136, shut: 142, t1: 148, q1: 158 };
const QUEUE = [0, 1, 2, 3].map(k => ({ k, x: 429 - k * 3, in: DOME.q0 + k * 3, col: ['#c84a3a', '#3a5a8a', '#e8d8b8', '#4a8a5a'][k] }));
function drawObservatoryLife(t, here) {
  const D = DOMES[2];
  if (t >= DOME.t0 && t < DOME.t1) {
    const turn = smooth(DOME.t0, DOME.open, t) * (1 - smooth(DOME.shut, DOME.t1, t));
    const open = smooth(DOME.open, DOME.tele, t) * (1 - smooth(DOME.close, DOME.shut, t));
    const su = 0.18 + 0.2 * turn;
    for (let y = Math.floor(D.cy - D.ry); y < D.cy; y++) for (let i = Math.floor(D.cx - D.rx); i <= Math.ceil(D.cx + D.rx); i++) {
      const col = domeColor(D, i, y, su, 0.12, open);
      if (col) cput(i, y, col);
    }
    if (open > 0.9) {                                                               // the telescope's tube, pointing at the moon
      const g = frac(t / 4) < 0.08;
      cput(Math.round(D.cx + su * D.rx), 67, '#e8e8f0'); cput(Math.round(D.cx + su * D.rx) + 1, 66, g ? '#ffffff' : '#c8ccd8');
      halo(HL.scope, D.cx + su * D.rx + 1, 66, g ? 0.9 : 0.4);
    }
  }
  if (!here(0) || t < DOME.q0 || t >= DOME.q1) return;
  for (const q of QUEUE) {                                                         // the queue on the terrace, behind its parapet
    const arrive = q.in + 4, leave = DOME.close + 2 + q.k * 2;
    if (t < q.in || t >= leave + 8) continue;
    const x = Math.round(t < arrive ? lerp(396, q.x, (t - q.in) / 4) : t < leave ? q.x : lerp(q.x, 380, (t - leave) / 8));
    cput(x, 80, '#e0b08a'); cput(x, 81, q.col); cput(x, 82, q.col); cput(x, 83, '#2a2430');
  }
}

/* ================= Big moment 3: fireworks over the Hollywood Bowl ================= */
// A Bowl night's finale, over the hills west of the sign: a dozen calm bursts in thirty seconds, the hills catching
// their colour. With reduced motion the bursts keep their sparks and lose their flash.
const FW_COLS = [['#fffbe8', '#ffd24a', '#ff8a2a'], ['#f0fffc', '#5cf2d8', '#1f9a8e'], ['#fff0fa', '#ff6ab4', '#b02a70'],
  ['#ffffff', '#c0ccff', '#7a6aff'], ['#f6ffe8', '#9cff6a', '#2a9a44'], ['#fff6f0', '#ff6a4a', '#b02a2a']];
const FW_LIFE = [2.8, 2.4, 3.8], FW_RISE = 1.3;
function fwDirs(b, id) {
  const d = new Float32Array(b.n * 2), rot = hash(id, 2, 60) * TAU;
  for (let i = 0; i < b.n; i++) {
    if (b.kind === 1) { const a = TAU * i / b.n + rot, rr = i % 3 === 0 ? 0.5 : 1; d[2 * i] = Math.cos(a) * rr; d[2 * i + 1] = Math.sin(a) * 0.84 * rr; }
    else { const z = 1 - 2 * (i + 0.5) / b.n, rr = Math.sqrt(1 - z * z), a = i * 2.39996 + rot, sp = 0.9 + 0.1 * hash(id, i, 62); d[2 * i] = Math.cos(a) * rr * sp; d[2 * i + 1] = Math.sin(a) * rr * sp; }
  }
  return d;
}
const BURSTS = [
  [170.2, 96, 58, 0, 0, 52, 18], [172.6, 150, 44, 1, 1, 56, 16], [175.0, 70, 66, 2, 0, 48, 15], [177.6, 128, 38, 3, 2, 60, 20],
  [180.2, 176, 60, 4, 0, 48, 15], [182.8, 86, 44, 5, 1, 56, 16], [185.4, 142, 64, 0, 0, 52, 17], [188.2, 62, 52, 2, 2, 60, 20],
  [190.8, 112, 34, 1, 0, 56, 18], [194.0, 80, 50, 3, 0, 60, 18], [194.6, 152, 42, 4, 1, 56, 16], [195.4, 116, 58, 2, 2, 60, 20],
].map(([t, x, y, c, kind, n, R], id) => ({ t, x, y, c, kind, n, R: R + 4, id, lx: x + Math.round((hash(id, 1, 60) - 0.5) * 14) })).map(b => ({ ...b, dirs: fwDirs(b, b.id) }));
function drawFireworks(t, reducedMotion) {
  const sp = step(t, 2400);
  for (const b of BURSTS) {
    const tau = t - b.t, life = FW_LIFE[b.kind];
    if (tau < -FW_RISE || tau > life) continue;
    if (tau < 0) {                                                                  // the rocket climbing from the Bowl
      const u = (tau + FW_RISE) / FW_RISE;
      for (let k = 6; k >= 0; k--) {
        const uu = u - k * 0.03; if (uu < 0) continue;
        const e = 1 - (1 - uu) * (1 - uu);
        ctx.globalAlpha = k ? 0.5 * (1 - k / 7) : 1;
        cput(Math.round(lerp(b.lx, b.x, uu)), Math.round(lerp(118, b.y, e) + k * 0.4), k ? '#ffb060' : '#fff6d8');
      }
      ctx.globalAlpha = 1;
      continue;
    }
    const pal = FW_COLS[b.c], u = tau / life, willow = b.kind === 2, k = willow ? 1.9 : 2.8, g = willow ? 8 : 4.5;
    if (tau < 0.35 && !reducedMotion) halo(HL.fwFlash, b.x, b.y, 1 - tau / 0.35);
    halo(HL.fwGlow[b.c], b.x, b.y + 16 + g * tau * tau * 0.6, 0.55 * (1 - u));      // the hills under it catch the colour
    const fade = u < 0.5 ? 1 : 1 - (u - 0.5) / 0.5, head = u < 0.1 ? pal[0] : u < 0.6 ? pal[1] : pal[2], trail = willow ? (u < 0.5 ? '#ffc860' : '#c87a30') : pal[2];
    const nT = willow ? 6 : 3, dT = willow ? 0.1 : 0.06, r0 = b.R * (1 - Math.exp(-k * tau)), dy0 = g * tau * tau;
    for (let i = 0; i < b.n; i++) {
      const dx = b.dirs[2 * i], dy = b.dirs[2 * i + 1];
      let a = fade;
      if (u > 0.45 && hash(b.id * 131 + i, sp, 63) < 0.35) a *= 0.2;
      ctx.fillStyle = trail;
      for (let j = nT; j >= 1; j--) {
        const tt = tau - j * dT; if (tt <= 0) continue;
        const r = b.R * (1 - Math.exp(-k * tt));
        ctx.globalAlpha = a * 0.5 * (1 - j / (nT + 1));
        ctx.fillRect(Math.round(b.x + dx * r), Math.round(b.y + dy * r + g * tt * tt), 1, 1);
      }
      ctx.fillStyle = b.kind === 1 && i % 3 === 0 ? (u < 0.55 ? '#ffffff' : pal[1]) : head; ctx.globalAlpha = a;
      ctx.fillRect(Math.round(b.x + dx * r0), Math.round(b.y + dy * r0 + dy0), 1, 1);
    }
  }
  ctx.globalAlpha = 1;
}

/* ================= Medium moments in the sky: a plane, the police helicopter ================= */
const PLANE = { t0: 28, t1: 94 };
function drawPlane(t) {
  if (t < PLANE.t0 || t >= PLANE.t1) return;
  const u = (t - PLANE.t0) / (PLANE.t1 - PLANE.t0), x = Math.round(lerp(286, 492, u)), y = Math.round(lerp(26, 11, u));
  rect(ctx, x - 1, y, 4, 1, '#1a1e30');
  px(ctx, x - 2, y, '#fff6e0'); halo(HL.landing, x - 2, y, 0.6);
  px(ctx, x + 2, y, frac(t / 1.5) < 0.5 ? '#ff4a4a' : '#5a1a1a');
  if (frac(t) < 0.1) { px(ctx, x, y - 1, '#ffffff'); halo(HL.strobe, x, y - 1, 0.7); }
}
const HELI = { t0: 212, t1: 236 };
function drawHeli(t) {
  if (t < HELI.t0 || t >= HELI.t1) return;
  const u = (t - HELI.t0) / (HELI.t1 - HELI.t0), x = Math.round(lerp(506, -24, u)), y = Math.round(lerp(102, 70, u) + wave(t, 60) * 0.6);
  const fade = smooth(0.2, 0.28, u) * (1 - smooth(0.92, 0.98, u));             // its spotlight comes on once past the billboard
  const aim = -10 + 8 * Math.sin(TAU * (t - HELI.t0) / 8);                         // the spotlight sweeps the slope below
  ctx.globalCompositeOperation = 'lighter';
  for (let k = 2; k < 34; k++) {
    const v = k / 34, cx = x + aim * v, hw = 0.5 + v * 3.2;
    ctx.globalAlpha = 0.16 * (1 - v * 0.6) * fade; ctx.fillStyle = '#e8f0ff';
    ctx.fillRect(Math.round(cx - hw), y + k, Math.round(hw * 2) + 1, 1);
  }
  ctx.globalAlpha = 1; ctx.globalCompositeOperation = 'source-over';
  halo(HL.heliPool, x + aim, y + 36, 0.8 * fade);
  rect(ctx, x - 3, y, 7, 2, '#14141c'); rect(ctx, x + 3, y, 5, 1, '#14141c'); px(ctx, x - 2, y, '#3a4058');
  rect(ctx, x - 5 + (step(t, 2400) & 1) * 2, y - 2, 9, 1, '#2a2a38');            // the rotor's blur
  if (frac(t / 1.2) < 0.15) { px(ctx, x + 7, y, '#ff4a4a'); halo(HL.red, x + 7, y, 0.7); }
  if (frac(t / 1.2 + 0.5) < 0.08) { px(ctx, x - 3, y + 1, '#ffffff'); halo(HL.strobe, x - 3, y + 1, 0.6); }
}

/* ================= Capitol Records' beacon spells HOLLYWOOD in Morse ================= */
// dot 1 unit, dash 3, gaps 1 within a letter, 3 between, 7 after the word: 118 units, sent 8 times a loop (30 s each)
const MORSE = (() => {
  const code = { H: '....', O: '---', L: '.-..', Y: '-.--', W: '.--', D: '-..' }, on = [];
  let u = 0;
  [...'HOLLYWOOD'].forEach((ch, li) => {
    [...code[ch]].forEach((s, k) => { const d = s === '.' ? 1 : 3; on.push([u, u + d]); u += d + (k < code[ch].length - 1 ? 1 : 0); });
    u += li < 8 ? 3 : 7;
  });
  return { on, units: u };
})();
const morseOn = t => { const v = frac(t / 30) * MORSE.units; return MORSE.on.some(([a, b]) => v >= a && v < b); };

/* ================= Small life: steam, the sign's light, the city's own falling leaves ================= */
function makePuff(r, light, dark) {
  const s = r * 2 + 1, p = painter(s, s);
  for (let y = 0; y < s; y++) for (let i = 0; i < s; i++) {
    const d = Math.hypot(i - r, y - r) / (r + 0.5);
    if (d < 1 && (1 - d) * 1.8 > bayer(i, y) * 0.95) p.put(i, y, i + y < r * 1.7 ? light : dark);
  }
  return p.done();
}
const VENT = [];
{ const r = rng(64); for (let i = 0; i < 14; i++) VENT.push({ n: 30 + Math.floor(r() * 8), ph: r(), k: 20 + Math.floor(r() * 20), p: r() }); }
function drawVentSteam(t) {                                                        // the kitchen's vent, blown west
  const g = gustX(t);
  for (const s of VENT) {
    const q = t * s.n / LOOP + s.ph, u = q - Math.floor(q), born = t - u * LOOP / s.n;
    const x = 29.5 - u * 20 - u * u * 18 + wave(t, s.k, s.p) * 1.5 * u + (g - gustX(born)) * 0.25, y = 138 - u * 26 + u * u * 8;
    const rr = Math.max(1, Math.min(5, 1 + Math.floor(u * 6)));
    ctx.globalAlpha = 0.35 * Math.min(1, u * 10) * Math.pow(1 - u, 1.3);
    ctx.drawImage(PUFF_C[rr], Math.round(x) - rr, Math.round(y) - rr);
  }
  ctx.globalAlpha = 1;
}
// a soft light that walks along the letters once a minute, the sign's slow motion
function drawSignLight(t) {
  const v = frac(t / 60 + 0.35);
  if (v > 0.12) return;
  const u = v / 0.12, x = SIGN.x + u * (SIGN_X1 - SIGN.x);
  halo(HL.shimmer, x, SIGN.y + 6, Math.sin(Math.PI * u) * 0.9);
}
// dry leaves off the sycamores in the canyons, carried west by the Santa Ana at three depths (own weather: 'leaves')
const DRY = [[], [], []];
{
  const r = rng(907);
  [[36, 3, 6, 96, 190, 1], [26, 5, 8, 150, 236, 2], [14, 7, 10, 110, 262, 3]].forEach(([count, nMin, nMax, y0, y1, size], d) => {
    for (let i = 0; i < count; i++) DRY[d].push({ id: d * 100 + i, n: nMin + Math.floor(r() * (nMax - nMin + 1)), ph: r(), y0, y1, size,
      k: 30 + Math.floor(r() * 30), p: r(), col: ['#b8844a', '#d8a050', '#a06a34', '#e0b060'][Math.floor(r() * 4)] });
  });
}
function drawLeaves(d, t) {
  const g = gustX(t), gk = [0.3, 0.6, 1][d];
  for (const L of DRY[d]) {
    const { cyc, u } = cycle(t, L.n, L.ph), born = t - u * LOOP / L.n;
    const x0 = W + 12 - u * (W + 40) + (g - gustX(born)) * gk;
    const x = ((x0 % (W + 40)) + W + 40) % (W + 40) - 20;
    const y = lerp(L.y0, L.y1, hash(L.id, cyc, 1)) + u * 22 * (d + 1) * 0.4 + Math.sin(TAU * (L.k * t / LOOP + L.p)) * (2 + d);
    const fl = Math.sin(TAU * (L.k * 2 * t / LOOP + L.p)) > 0;
    ctx.fillStyle = L.col;
    if (L.size === 1) ctx.fillRect(Math.round(x), Math.round(y), 1, 1);
    else if (L.size === 2) ctx.fillRect(Math.round(x), Math.round(y), fl ? 2 : 1, fl ? 1 : 2);
    else { ctx.fillRect(Math.round(x), Math.round(y), fl ? 4 : 2, fl ? 2 : 3); ctx.fillRect(Math.round(x) + 1, Math.round(y) + (fl ? -1 : 1), 2, 1); ctx.fillStyle = '#6a4224'; ctx.fillRect(Math.round(x) + 1, Math.round(y) + 1, 1, 1); }
  }
}

/* ================= Boulevard traffic ================= */
// Side views drawn from profiles in metres (x from the front bumper, y up from the road), rasterised at each lane's
// scale: the westbound lane by the north kerb, the eastbound lane nearer us, and the bus pulling in to its stop.
const SEDAN = { L: 4.7, body: [[0, 0.32], [0.02, 0.72], [0.25, 0.86], [1.15, 0.95], [1.75, 1.0], [2.25, 1.42], [3.35, 1.44], [3.95, 1.05], [4.6, 0.98], [4.7, 0.75], [4.7, 0.32]],
  glass: [[1.85, 1.0], [2.3, 1.36], [3.3, 1.38], [3.85, 1.04]], pillars: [2.8], wheels: [0.82, 3.85], wr: 0.33, head: 0.72, tail: 0.84 };
const VEH = {
  sedan: SEDAN,
  suv: { L: 4.85, body: [[0, 0.42], [0.02, 0.86], [0.3, 1.0], [1.2, 1.08], [1.6, 1.12], [2.05, 1.74], [4.6, 1.76], [4.8, 1.25], [4.85, 0.9], [4.85, 0.42]],
    glass: [[1.72, 1.14], [2.12, 1.68], [4.52, 1.7], [4.66, 1.18]], pillars: [2.95, 3.85], wheels: [0.9, 3.95], wr: 0.38, head: 0.86, tail: 1.1 },
  pickup: { L: 5.4, body: [[0, 0.45], [0.02, 0.9], [0.3, 1.05], [1.4, 1.12], [1.7, 1.18], [2.1, 1.75], [3.05, 1.76], [3.15, 1.22], [5.35, 1.22], [5.4, 1.15], [5.4, 0.45]],
    glass: [[1.82, 1.2], [2.17, 1.68], [2.98, 1.68], [3.04, 1.24]], pillars: [], wheels: [0.95, 4.35], wr: 0.4, head: 0.9, tail: 1.0 },
  police: { ...SEDAN, paint: 'lapd' },
  cab: { ...SEDAN, paint: 'cab' },
  bus: { L: 12, body: [[0, 0.38], [0, 2.85], [0.18, 3.05], [11.85, 3.05], [12, 2.9], [12, 0.38]], wheels: [2.1, 9.4], wr: 0.5, head: 0.7, tail: 0.9, paint: 'bus' },
  cruiser: { L: 5.3, body: [[0, 0.3], [0.02, 0.68], [0.35, 0.8], [1.9, 0.9], [2.05, 0.93], [4.55, 0.93], [5.0, 1.0], [5.3, 1.12], [5.3, 0.75], [5.22, 0.3]],
    wheels: [1.0, 4.35], wr: 0.33, head: 0.66, tail: 0.92, paint: 'cruiser' },
};
const LANES = { w: { y: 226, s: 17.0, dir: -1 }, e: { y: 247, s: 21.3, dir: 1 }, curb: { y: 216, s: 14.6, dir: -1 } };
function inPoly(poly, x, y) {
  let inside = false;
  for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
    const [xi, yi] = poly[i], [xj, yj] = poly[j];
    if ((yi > y) !== (yj > y) && x < (xj - xi) * (y - yi) / (yj - yi) + xi) inside = !inside;
  }
  return inside;
}
// a vehicle facing west (its front on the left); passengers: whether the bus's riders show
function renderVehicle(kind, s, col, passengers = true) {
  const V = VEH[kind], top = Math.max(...V.body.map(q => q[1])) + (V.paint === 'lapd' || V.paint === 'cab' ? 0.14 : 0);
  const w = Math.ceil(V.L * s) + 1, h = Math.ceil(top * s) + 1, p = painter(w, h);
  const base = V.paint === 'lapd' ? '#141418' : V.paint === 'cab' ? '#e2b02e' : V.paint === 'bus' ? '#d8682a' : V.paint === 'cruiser' ? '#86c6dc' : col;
  for (let j = 0; j < h; j++) for (let i = 0; i < w; i++) {
    const X = (i + 0.5) / s, Y = (h - j - 0.5) / s;
    let c = null;
    for (const wx of V.wheels) {
      const d = Math.hypot(X - wx, Y - V.wr);
      if (d < V.wr) c = d < V.wr * 0.3 ? '#c8ccd4' : d < V.wr * 0.62 ? '#6a707c' : '#0c0b10';
      else if (!c && d < V.wr + 0.1 && inPoly(V.body, X, Y)) c = '#0a090e';         // the wheel arch
    }
    if (!c && inPoly(V.body, X, Y)) {
      const edge = !inPoly(V.body, X, Y + 0.1);
      c = edge ? tint(base, '#ffe8c8', 0.35) : Y < 0.52 ? tint(base, '#000000', 0.35) : base;
      if (!edge && Y > 0.84 && Y < 0.9 && V.paint !== 'bus') c = tint(base, '#ffffff', 0.15);   // the crease along the flank
      if (V.glass && inPoly(V.glass, X, Y)) {
        c = frac(X * 1.3 + Y * 0.8) < 0.18 ? '#36425a' : '#141a26';
        if (V.pillars.some(px0 => Math.abs(X - px0) < 0.07)) c = tint(base, '#000000', 0.4);
      }
      if (V.paint === 'lapd' && !edge && X > 1.62 && X < 3.95 && Y < 0.97 && Y > 0.36) c = '#ececf0';   // the white doors
      if (V.paint === 'lapd' && Math.hypot(X - 2.25, Y - 0.72) < 0.07) c = '#d8b048';                 // the badge
      if (V.paint === 'bus') {
        if (Y > 1.3 && Y < 2.5 && X > 0.45 && X < 11.6) {                           // the windows and their riders
          const pane = Math.floor((X - 0.45) / 1.3), u = (X - 0.45) / 1.3 - pane;
          c = u < 0.1 ? '#2a2a30' : '#e8c890';
          if (u >= 0.1 && Y < 2.38 && Y > 1.32) c = Y > 2.2 ? '#f4dcaa' : '#d8b478';
          if (passengers && u > 0.35 && u < 0.62 && Y < 1.95 && hash(pane, 4, 71) < 0.55) c = Y > 1.75 ? '#3a2a24' : '#2a3048';
        }
        if (Y > 2.75) c = '#e8e6e0';                                                // the white roof band
        if (X < 0.32 && Y > 0.95 && Y < 2.75) c = frac(Y * 2.2) < 0.2 ? '#4a5878' : '#232c40';   // the windscreen
        if (X > 0.4 && X < 2.6 && Y > 2.55 && Y < 2.73) c = (i & 1) ? '#ffb030' : '#5a3a10';      // the destination strip
        if ((X > 0.55 && X < 1.5 || X > 6.2 && X < 7.1) && Y > 0.42 && Y < 2.5) c = Math.abs(X - (X < 2 ? 1.02 : 6.65)) < 0.04 ? '#1a1a22' : (Y < 1.3 ? '#2a3040' : c);   // doors
      }
      if (V.paint === 'cruiser') {
        if (X > 3.0 && X < 4.95 && Y > 0.48 && Y < 0.74) c = '#eef2f4';            // the white cove
        if (Y > 0.74 && Y < 0.79 && X > 0.4 && X < 3.0) c = '#e8ecf2';             // the chrome spear
      }
    }
    if (V.paint === 'cruiser' && !c) {
      if (Math.abs((X - 2.05) * 0.37 - (Y - 0.93) * 0.3) < 0.025 && Y > 0.93 && Y < 1.32) c = '#d8dce4';   // the windscreen's frame
      else if (X > 2.6 && X < 4.4 && Y >= 0.93 && Y < 1.08 && !(X > 3.3 && X < 3.5)) c = Y > 1.03 ? '#f6ecd8' : '#d8c8a8';   // the seats
    }
    if (V.paint === 'lapd' && !c && X > 2.45 && X < 3.25 && Y > 1.44 && Y < 1.58) c = X < 2.62 ? '#6a1a1a' : X > 3.08 ? '#1a2a6a' : '#3a3a48';   // the light bar
    if (V.paint === 'cab' && !c && X > 2.6 && X < 3.05 && Y > 1.44 && Y < 1.58) c = '#f8f0d0';
    if (c && X < 0.1 && Math.abs(Y - V.head) < 0.07) c = '#fff6dc';
    if (c && X > V.L - 0.1 && Math.abs(Y - V.tail) < 0.08) c = '#ff2a2a';
    if (c) p.put(i, j, c);
  }
  return p.done();
}
function flipCanvas(src) {
  const [c, x] = makeCanvas(src.width, src.height);
  x.translate(src.width, 0); x.scale(-1, 1); x.drawImage(src, 0, 0);
  return c;
}
// headlights' light on the road ahead, pointing left; flipped for the eastbound lane
function renderRoadBeam(len, hgt) {
  const p = painter(len, hgt);
  for (let j = 0; j < hgt; j++) for (let i = 0; i < len; i++) {
    const u = 1 - i / len, dy = (j - hgt * 0.35) / (hgt * 0.65), a = 0.16 * Math.pow(u, 0.9) * Math.max(0, 1 - dy * dy) + 0.14 * Math.max(0, 1 - Math.hypot((i - len * 0.6) / (len * 0.3), (j - hgt * 0.6) / (hgt * 0.4)));
    const q = Math.floor(a * 30 + bayer(i, j)) / 30;
    if (q > 0) p.put(len - 1 - i, j, '#fff0c8', Math.round(Math.min(1, q) * 255));
  }
  return p.done();
}
// [t0, duration, lane, kind, colour]: a car about every 9 s, alternating lanes; the bus and the cruise are below
const TRAFFIC = [
  [3, 7.5, 'w', 'sedan', '#9aa0aa'], [21, 7, 'w', 'suv', '#1e2026'], [39, 7.5, 'w', 'cab'], [58, 8, 'w', 'pickup', '#3a4a5a'],
  [112, 7, 'w', 'sedan', '#e8e4dc'], [140, 7.5, 'w', 'suv', '#6a2028'], [158, 8.5, 'w', 'police'], [178, 7, 'w', 'sedan', '#26324a'],
  [196, 7.5, 'w', 'sedan', '#7a7e86'], [214, 7, 'w', 'suv', '#2e4a3a'], [229, 7.5, 'w', 'sedan', '#5a2a2a'],
  [9, 6.5, 'e', 'sedan', '#7a2026'], [29, 6.5, 'e', 'sedan', '#2a2a30'], [47, 7, 'e', 'suv', '#d8d8d0'], [66, 6.5, 'e', 'sedan', '#4a5a6a'],
  [92, 7, 'e', 'pickup', '#8a7a5a'], [146, 6.5, 'e', 'sedan', '#3a3e48'], [168, 7, 'e', 'cab'], [186, 6.5, 'e', 'suv', '#5a6a7a'],
  [204, 6.5, 'e', 'sedan', '#9a3a2a'], [222, 7, 'e', 'sedan', '#c8c8c0'],
].map(([t0, dur, lane, kind, col]) => ({ t0, dur, lane, kind, col }));
const BUS = { t0: 74, arrive: 84, leave: 93, t1: 107, stopX: 418 };              // the local bus: pulls in, waits, pulls out
const CRUISE = { t0: 118, dur: 16 };                                             // a two-tone convertible, top down, slowly east
let VEH_SPR;
function prerenderTraffic() {
  VEH_SPR = { list: TRAFFIC.map(v => { const c = renderVehicle(v.kind, LANES[v.lane].s, v.col); return LANES[v.lane].dir > 0 ? flipCanvas(c) : c; }) };
  VEH_SPR.bus = renderVehicle('bus', LANES.curb.s, null, true); VEH_SPR.busBare = renderVehicle('bus', LANES.curb.s, null, false);
  VEH_SPR.cruise = flipCanvas(renderVehicle('cruiser', LANES.e.s, null));
  VEH_SPR.beam = { w: renderRoadBeam(70, 9), e: flipCanvas(renderRoadBeam(90, 11)), curb: renderRoadBeam(60, 8) };
}
// one vehicle at x (its left edge) in a lane: its mirror on the asphalt, its headlights' light on the road, its lamps
function drawVehicle(spr, lane, x, t, head = true) {
  const L = LANES[lane], y = L.y - spr.height, w = spr.width, dir = L.dir;
  for (let j = 0; j < 6; j++) { ctx.globalAlpha = 0.24 * (1 - j / 6); ctx.drawImage(spr, 0, spr.height - 1 - j, w, 1, x, L.y + 1 + j, w, 1); }
  ctx.globalAlpha = 0.65; ctx.fillStyle = '#05040a'; ctx.fillRect(Math.round(x) + 2, L.y, w - 4, 1);   // its shadow on the road
  ctx.globalAlpha = 1;
  const fx = dir > 0 ? x + w : x, bx = dir > 0 ? x : x + w, hy = L.y - Math.round(0.72 * L.s);
  if (head) {
    const bm = VEH_SPR.beam[lane];
    ctx.globalCompositeOperation = 'lighter';
    ctx.drawImage(bm, dir > 0 ? fx : fx - bm.width, L.y - bm.height + 3);
    ctx.globalCompositeOperation = 'source-over';
  }
  ctx.drawImage(spr, Math.round(x), y);
  if (head) { halo(HL.head, fx - dir, hy, 1); streak(ctx, t, fx + dir * 6, 4, '#fff0c0', 0.22, 30 + lane.length, L.y + 1, Math.min(H, L.y + 14)); }
  halo(HL.tail, bx + dir, hy - Math.round(0.1 * L.s), 0.85);
  streak(ctx, t, bx, 2, '#ff4030', 0.3, 31, L.y + 1, Math.min(H, L.y + 10));
}
function drawTraffic(t, here) {
  // the bus at the curb lane first (it is the farthest), then the westbound lane, then the eastbound one
  if (t >= BUS.t0 && t < BUS.t1) {
    const sw = VEH_SPR.bus.width;
    const x = t < BUS.arrive ? lerp(W + 4, BUS.stopX, 1 - (1 - (t - BUS.t0) / (BUS.arrive - BUS.t0)) ** 2)
      : t < BUS.leave ? BUS.stopX : lerp(BUS.stopX, -sw - 6, ((t - BUS.leave) / (BUS.t1 - BUS.leave)) ** 1.6);
    drawVehicle(here(0) ? VEH_SPR.bus : VEH_SPR.busBare, 'curb', Math.round(x), t);
  }
  for (const lane of ['w', 'e']) {
    TRAFFIC.forEach((v, k) => {
      if (v.lane !== lane || t < v.t0 || t >= v.t0 + v.dur) return;
      const spr = VEH_SPR.list[k], u = (t - v.t0) / v.dur, dir = LANES[lane].dir;
      drawVehicle(spr, lane, Math.round(dir > 0 ? lerp(-spr.width - 4, W + 4, u) : lerp(W + 4, -spr.width - 4, u)), t);
    });
    if (lane === 'e' && here(0) && t >= CRUISE.t0 && t < CRUISE.t0 + CRUISE.dur) drawCruise(t);
  }
}
// the convertible cruising east: a couple in it, her scarf flying, his arm out on the door
function drawCruise(t) {
  const spr = VEH_SPR.cruise, u = (t - CRUISE.t0) / CRUISE.dur, x = Math.round(lerp(-spr.width - 4, W + 4, u)), s = LANES.e.s, y0 = LANES.e.y;
  drawVehicle(spr, 'e', x, t);
  const seat = Y => y0 - Math.round(Y * s), w = spr.width;
  for (const [X, hair, skin, shirt] of [[3.35, '#2a1a14', '#c88a64', '#f0f0ec'], [2.75, '#d8a040', '#e8b490', '#e85a7a']]) {
    const hx = x + w - Math.round(X * s);                                           // the sprite is flipped: X counts from the right
    rect(ctx, hx - 2, seat(1.16), 5, Math.round(0.2 * s), shirt);
    rect(ctx, hx - 1, seat(1.42), 4, 4, skin); rect(ctx, hx - 1, seat(1.42), 4, 1, hair); rect(ctx, hx - 2, seat(1.42), 1, 3, hair);
  }
  const sx = x + w - Math.round(2.75 * s) - 3, sy = seat(1.32), fl = step(t, 1440) & 1;   // her scarf streaming back
  for (let k = 0; k < 6; k++) cput(sx - k, sy + (k > 2 ? fl : 0) + (k > 4 ? 1 : 0), k & 1 ? '#ffd040' : '#ffe880');
  if (frac(t / 3) < 0.5) for (let k = 0; k < 3; k++) cput(x + w - Math.round(4.2 * s) + k * 3, seat(1.7) - k, '#ffe6b0');   // music from the radio
}

/* ================= People on the north sidewalk ================= */
// One function draws every person: 20 rows from hair to shoes (the sidewalk lies 21 to 22 m off, about 12 px a metre),
// in profile (view 1 east, -1 west; fr the stride) or from behind (view 0), with what the arms are doing.
const LOOK = {
  skater: { hair: '#2a1a14', skin: '#a8724e', top: '#3a7ab8', top2: '#2a5a8a', legs: '#2a2a34', legs2: '#1e1e28', cap: '#1a1a20' },
  dogWalker: { hair: '#2a1a14', skin: '#c88a64', top: '#d8b048', top2: '#a8822e', legs: '#2a3a5a', legs2: '#1e2a44' },
  her: { hair: '#1a1214', skin: '#e0a882', top: '#2a8a8a', top2: '#1e6a6a', legs: '#e0a882', legs2: '#c08a6a', long: 1, skirt: 1 },
  him: { hair: '#3a2418', skin: '#b07a58', top: '#e8e0d4', top2: '#b8b0a4', legs: '#3a3440', legs2: '#2a2430' },
  bag: { hair: '#8a5a3a', skin: '#e8b490', top: '#8a3a5a', top2: '#6a2a46', legs: '#2a2430', legs2: '#1e1a24', long: 1 },
  jogger: { hair: '#1a1214', skin: '#7a4a34', top: '#e85a3a', top2: '#b84228', legs: '#1e1e28', legs2: '#16161e' },
  exiter: { hair: '#5a3a2a', skin: '#e0a882', top: '#3a5a3a', top2: '#2a4a2a', legs: '#4a4a5a', legs2: '#3a3a4a', cap: '#c8c8c0' },
  in1: { hair: '#c8a060', skin: '#e8b490', top: '#e8d8b8', top2: '#c8b898', legs: '#3a5a8a', legs2: '#2a4a7a', long: 1 },
  in2: { hair: '#1a1214', skin: '#8a5a3e', top: '#4a4a52', top2: '#3a3a42', legs: '#2a2a34', legs2: '#1e1e28' },
  cupA: { hair: '#3a2418', skin: '#c88a64', top: '#6a3a7a', top2: '#4a2a5a', legs: '#2a2a34', legs2: '#1e1e28', long: 1 },
  cupB: { hair: '#c8c4c0', skin: '#e0a882', top: '#2a4a6a', top2: '#1e3a5a', legs: '#5a4a3a', legs2: '#4a3a2a' },
  rider: { hair: '#1a1012', skin: '#a87050', top: '#8a3a5a', top2: '#6a2a46', legs: '#2a2a3a', legs2: '#1e1e2a', long: 1 },
};
function fig(x, foot, L, view, fr = 0, arm = 'down') {
  const top = foot - 19;
  if (view === 0) {                                                              // from behind, five wide
    rect(ctx, x + 1, top, 3, 4, L.hair);
    if (L.long) rect(ctx, x + 1, top + 4, 3, 2, L.hair);
    rect(ctx, x, top + 4, 5, 8, L.top); rect(ctx, x, top + 5, 1, 6, L.top2); rect(ctx, x + 4, top + 5, 1, 6, L.top2);
    rect(ctx, x + 1, top + 4, 3, 1, tint(L.top, '#ffd8a0', 0.25));               // the street's light on the shoulders
    if (L.skirt) { rect(ctx, x, top + 12, 5, 3, L.top); rect(ctx, x + 1, top + 15, 1, 4, L.legs); rect(ctx, x + 3, top + 15, 1, 4, L.legs); }
    else { rect(ctx, x, top + 12, 2, 7, L.legs); rect(ctx, x + 3, top + 12, 2, 7, L.legs2); }
    rect(ctx, x, top + 19, 2, 1, '#141018'); rect(ctx, x + 3, top + 19, 2, 1, '#141018');
    if (arm === 'phone') { rect(ctx, x + 4, top + 1, 1, 4, L.top2); rect(ctx, x + 2, top - 2, 3, 3, '#1a1a22'); rect(ctx, x + 3, top - 2, 2, 2, '#cfe8ff'); }
    if (arm === 'point') { for (let k = 0; k < 5; k++) cput(x + 4 + (k >> 1), top + 4 - k, L.top2); cput(x + 6, top - 1, L.skin); }
    return;
  }
  const f = (i, y, w, h, c) => rect(ctx, view > 0 ? x + i : x + 5 - i - w, top + y, w, h, c);
  f(1, 0, 3, 1, L.cap || L.hair); if (L.cap) f(3, 0, 2, 1, L.cap);
  f(1, 1, 1, 3, L.hair); f(2, 1, 2, 3, L.skin); f(4, 2, 1, 1, L.skin);
  if (L.long) f(0, 1, 1, 6, L.hair);
  f(0, 4, 4, 8, L.top); f(0, 4, 4, 1, tint(L.top, '#ffd8a0', 0.25));
  if (arm === 'cup') { f(3, 6, 2, 1, L.top2); f(4, 4, 1, 2, L.top2); f(4, 3, 2, 1, '#f4f0e8'); f(5, 2, 1, 1, '#c8a080'); }
  else if (arm === 'wave') { f(3, 1, 1, 4, L.top2); f(3, 0, 1, 1, L.skin); }
  else { const sw = fr ? 1 : -1; f(2, 5, 1, 4, L.top2); f(2 + sw, 9, 1, 2, L.top2); f(2 + sw, 11, 1, 1, L.skin); }
  if (L.skirt) { f(0, 12, 5, 3, L.top); f(1 + (fr ? 1 : 0), 15, 1, 4, L.legs); f(3 - (fr ? 1 : 0), 15, 1, 4, L.legs2); }
  else for (let y = 12; y <= 18; y++) {
    const s = (y - 12) / 6, a = Math.round((fr ? 1.6 : 0.4) * s), b = Math.round((fr ? -1.4 : -0.3) * s);
    f(1 + b, y, 1, 1, L.legs2); f(2 + a, y, 2, 1, L.legs);
  }
  f(2 + (fr ? 2 : 1), 19, 2, 1, '#141018'); f(fr ? 0 : 1, 19, 1, 1, '#141018');
}
const DOG = [['......kk', '.....kke', 'tkkkkkk.', '.k...k..'], ['......kk', '.....kke', 'tkkkkkk.', '..k.k...']];
function dog(x, foot, dir, fr) {
  DOG[fr].forEach((row, j) => { for (let i = 0; i < row.length; i++) if (row[i] !== '.') cput(dir > 0 ? x + i : x + 7 - i, foot - 3 + j, row[i] === 'e' ? '#1a0c08' : '#8a4a24'); });
}
// people crossing on the sidewalk: [t0, duration, dir, feet row, look, kind, rank]
const PASSES = [
  [5, 9, 1, 206, 'skater', 'skate', 0.12], [132, 9, -1, 206, 'skater', 'skate', 0.12],
  [30, 46, 1, 205, 'dogWalker', 'dog', 0.42], [182, 46, -1, 205, 'dogWalker', 'dog', 0.42],
  [62, 40, -1, 203, 'bag', 'walk', 0.78], [96, 15, 1, 206, 'jogger', 'jog', 0.9], [208, 15, -1, 206, 'jogger', 'jog', 0.9],
].map(([t0, dur, dir, y, look, kind, rank]) => ({ t0, dur, dir, y, look, kind, rank }));
const TOUR = { in: 12, stop: 26, phone: [28, 38], point: [33, 40], go: 42, out: 80, x: 316, rank: 0.2 };   // tourists photograph the sign
const EXIT = { t0: 49.5, out: 51, t1: 86, rank: 0.48 }, ENTER = { t0: 204, door: 226, t1: 228.5, rank: 0.3 };
const BENCH = { x: 452, stand: 85, gone: 88, back: 192, sit: 206 };              // the woman at the bus stop (rank 0)
const PAIR = { rank: 0.62 };                                                     // two friends with coffee by the stone wall
function drawSidewalk(t, here) {
  const DL = [], put = (foot, fn) => DL.push([foot, fn]), st = step(t, 768) & 1, run = step(t, 1440) & 1;
  for (const P of PASSES) {
    if (t < P.t0 || t >= P.t0 + P.dur || !here(P.rank)) continue;
    const u = (t - P.t0) / P.dur, x = Math.round(P.dir > 0 ? lerp(-24, W + 14, u) : lerp(W + 14, -24, u)), L = LOOK[P.look];
    if (P.kind === 'skate') put(P.y, () => {                                     // riding, a push every 2 s
      const push = frac(t / 2) < 0.3;
      rect(ctx, x - 1, P.y - 2, 8, 1, '#3a2418'); cput(x, P.y - 1, '#c8c8c0'); cput(x + 5, P.y - 1, '#c8c8c0');
      fig(x, P.y - 3, L, P.dir, push ? run : 0, 'down');
      if (push) cput(x + (P.dir > 0 ? -2 : 7), P.y - 1, L.legs);
    });
    else if (P.kind === 'dog') put(P.y, () => {
      const dx = x + P.dir * 12;
      fig(x, P.y, L, P.dir, st, 'down');
      line(ctx, x + (P.dir > 0 ? 4 : 1), P.y - 9, dx + (P.dir > 0 ? 6 : 1), P.y - 3, '#c84a3a');
      dog(dx, P.y, P.dir, st);
    });
    else put(P.y, () => fig(x, P.y, L, P.dir, P.kind === 'jog' ? run : st, 'down'));
  }
  if (here(TOUR.rank) && t >= TOUR.in && t < TOUR.out) {                         // the tourists: in from the east, a photo, off west
    const walkIn = t < TOUR.stop, walkOut = t >= TOUR.go;
    const ox = walkIn ? lerp(W + 14, TOUR.x, (t - TOUR.in) / (TOUR.stop - TOUR.in)) : walkOut ? lerp(TOUR.x, -30, (t - TOUR.go) / (TOUR.out - TOUR.go)) : TOUR.x;
    const x = Math.round(ox), moving = walkIn || walkOut;
    put(205, () => fig(x, 205, LOOK.her, moving ? -1 : 0, st, !moving && inEvent(t, [TOUR.point]) ? 'point' : 'down'));
    put(206, () => fig(x + 7, 206, LOOK.him, moving ? -1 : 0, st ^ 1, !moving && inEvent(t, [TOUR.phone]) ? 'phone' : 'down'));
  }
  if (here(EXIT.rank) && t >= EXIT.t0 && t < EXIT.t1) {                         // a diner customer leaves, east along the walk
    const x = t < EXIT.out ? 101 : Math.round(lerp(101, W + 14, (t - EXIT.out) / (EXIT.t1 - EXIT.out)));
    put(t < EXIT.out ? 199 : 204, () => fig(x, t < EXIT.out ? 199 : 204, LOOK.exiter, 1, t < EXIT.out ? 0 : st, 'down'));
  }
  if (here(ENTER.rank) && t >= ENTER.t0 && t < ENTER.t1) {                      // a couple strolls in from the west and goes in
    const x = Math.round(t < ENTER.door ? lerp(-26, 92, (t - ENTER.t0) / (ENTER.door - ENTER.t0)) : lerp(92, 100, (t - ENTER.door) / (ENTER.t1 - ENTER.door)));
    const a = t < ENTER.door ? 1 : 1 - (t - ENTER.door) / (ENTER.t1 - ENTER.door);
    put(204, () => { ctx.globalAlpha = a; fig(x, 204, LOOK.in1, 1, st, 'down'); fig(x - 7, 205, LOOK.in2, 1, st ^ 1, 'down'); ctx.globalAlpha = 1; });
  }
  if (here(PAIR.rank)) {                                                         // two friends with coffee under the sign
    const sipA = frac(t / 20 + 0.2) < 0.12, nod = frac(t / 8) < 0.5 ? 0 : 1;
    put(203, () => { fig(127, 203, LOOK.cupA, 1, 0, sipA ? 'cup' : 'down'); fig(135, 203 + nod * 0, LOOK.cupB, -1, 0, 'cup'); });
  }
  if (here(0)) {                                                                // the woman waiting for the bus, her phone's light
    if (t < BENCH.stand || t >= BENCH.sit) put(204, () => drawBenchSitter(t));
    else if (t < BENCH.gone) put(204, () => fig(BENCH.x, 204, LOOK.rider, -1, 0, 'down'));
    else if (t >= BENCH.back) put(204, () => fig(Math.round(lerp(W + 10, BENCH.x, (t - BENCH.back) / (BENCH.sit - BENCH.back))), 204, LOOK.rider, -1, st, 'down'));
  }
  DL.sort((a, b) => a[0] - b[0]);
  for (const [, fn] of DL) fn();
}
const SIT = ['..hhh..', '.hhhss.', '.hhsss.', '.hh.s..', '.cccc..', 'ccccc..', 'ccccsP.', 'cccccc.', 'cccccc.', '.ppppppp', '.ppppppp', '......p.', '......p.', '......p.', '......p.', '.....bb.'];
const SIT_K = { h: '#1a1012', s: '#a87050', c: '#8a3a5a', p: '#2a2a3a', b: '#141018', P: '#bfe4ff' };
function drawBenchSitter(t) {
  const glow = 0.75 + 0.25 * (hash(5, step(t, 240), 3) < 0.7 ? 1 : 0.4);
  SIT.forEach((row, j) => { for (let i = 0; i < row.length; i++) if (SIT_K[row[i]]) cput(BENCH.x + 4 + i, 189 + j, row[i] === 'P' && glow < 0.9 ? '#8ab0cc' : SIT_K[row[i]]); });
  halo(HL.phone, BENCH.x + 10, 195, glow);
}
// the shop's cat on the window sill: her tail swishes, she looks round now and then, and blinks
const CAT = ['.k...k.', '.kk.kk.', '.kkkkk.', '.kGkGk.', '.kkkkk.', '..kkk..', '.kkkkk.', 'kkkkkkk', 'kkkkkkkk', 'kkkkkkkk', '.kkkkkk.'];
function drawCat(t) {
  const look = frac(t / 20 + 0.4) < 0.2 ? 1 : 0, blink = frac(t / 6 + 0.1) < 0.04;
  CAT.forEach((row, j) => { for (let i = 0; i < row.length; i++) if (row[i] !== '.') cput(439 + i + (j < 6 ? look : 0), 181 + j, row[i] === 'G' ? (blink ? '#0e0a10' : '#c8e05a') : '#0e0a10'); });
  const sw = frac(t / 6) < 0.5 ? 0 : 1;                                          // the tail
  for (const [dx, dy] of sw ? [[8, 9], [9, 8], [10, 7], [10, 6]] : [[8, 9], [9, 10], [10, 10], [11, 9]]) cput(439 + dx, 181 + dy, '#0e0a10');
}

/* ================= The loop's timeline =================
   all loop   palms swaying west in the Santa Ana, clouds drifting, stars, the masts' and the towers' red lamps, Capitol's
              beacon spelling HOLLYWOOD in Morse (30 s), Saturn's ring of light orbiting (8 s), windows on and off,
              a car up or down the side street every ~20 s, a car along the boulevard every ~9 s, the diner's
              cast (coffee poured every 16 s, the regular's sips every 24 s), the cat's tail, the kitchen's steam
   small      the skateboarder (5, 132), the jogger (96, 208), a woman with a bag (62), the dog walker (30, 182),
              a customer leaves the diner (49), a couple goes in (204-228), the light walking along the sign (every 60 s),
              Saturn's sign buzzing (33, 141, 206.5)
   medium     the gusts (44, 118, 196: palms lean, leaves fly), a plane towards Burbank (28-94), the bus pulls in for the
              woman at the stop and leaves (74-107), the LAPD cruiser (158), the police helicopter's spotlight on the
              hills (212-236), the tourists photographing the sign (12-80)
   big        the premiere's searchlights (18-70), the observatory opens its east dome to the moon while a queue forms on
              the terrace (90-158), the cruise of a two-tone convertible (118-134), fireworks over the Hollywood Bowl
              (169-199) */

/* ================= Weather ================= */
// Los Angeles draws its own dry leaves on the Santa Ana; a picked rain, drizzle, storm or snow falls instead: far drops end
// at the foot of the hills, mid ones on the north sidewalk and the boulevard, near ones past the bottom of the frame, and
// they catch the neon, the windows and the lamps.
const WEATHER = weatherPlans(['leaves']);
const WX_LAND = [[96, 150], [196, SOUTH], [H + 4, H + 4]];
const WX_LIGHTS = [
  { x: SAT.x, y: SAT.y + 22, rx: 26, ry: 34, col: '#ff8ac0' }, { x: 56, y: 176, rx: 66, ry: 20, col: '#ffd090' },
  { x: LAMP.x, y: LAMP.top + 8, rx: 16, ry: 26 }, { x: 392, y: 178, rx: 24, ry: 14, col: '#ffc070' },
  ...AV_LAMPS.filter(l => l.z < 70).map(l => { const [x, y] = proj(l.X - Math.sign(l.X) * 1.2, l.z, 7); return { x, y: y + 4, rx: 7, ry: 10 }; }),
];
const WX = { land: WX_LAND, lights: WX_LIGHTS };
const WX_BB = { land: WX_LAND, lights: [...WX_LIGHTS, { x: BB.x + (BB.w >> 1), y: BB.y + 4, rx: 26, ry: 10 }] };

/* ================= Pre-render everything static ================= */
const CROWD = 0.5;                                  // the share of the people shown by default; render(t, { crowd }) thins them
let FLATS_C, HL, SKY_C, MOON_C, CLOUDS, HILLS_C, SIGN_C, OBS_R, CAP_R, AVE_C, GLOW_C, DINER_IN, DINER_OUT, PYLON_C, PYLON_OFF,
  SHOP_C, STOP_C, BB_C, BB_AD, PITCH, CAR_C, STREET_C, VIG_C, SBEAM_C, PUFF_C;
function prerender() {
  HL = {
    moon: makeHalo(54, '#9aa4ff', 0.2, 1.8), moonCore: makeHalo(20, '#f2f0ff', 0.3, 1.5),
    sign: makeHalo(70, '#ffe2b4', 0.12, 1.5), signCore: makeHalo(34, '#fff4dc', 0.14, 1.3), shimmer: makeHalo(12, '#fff2d0', 0.35, 1.3),
    obs: makeHalo(52, '#ffd8a8', 0.11, 1.6), obsDoor: makeHalo(8, '#ffd08a', 0.4, 1.3), scope: makeHalo(4, '#e8f0ff', 0.6, 1.2), red: makeHalo(4, '#ff3030', 0.6, 1),
    capitol: makeHalo(30, '#ffd8b0', 0.07, 1.6), roadLamp: makeHalo(3, '#ffe2a8', 0.2, 1.2), avLamp: makeHalo(8, '#ffcf88', 0.4, 1.3),
    avBeam: makeHalo(6, '#fff0c8', 0.3, 1.3), head: makeHalo(7, '#fff4d0', 0.5, 1.3), tail: makeHalo(4, '#ff3020', 0.5, 1.2),
    pink: makeHalo(34, '#ff4a9a', 0.2, 1.5), aqua: makeHalo(16, '#3ae0d0', 0.2, 1.4), saturn: makeHalo(20, '#ffa04a', 0.22, 1.4),
    star: makeHalo(6, '#fff4b0', 0.5, 1.2), diner: makeHalo(46, '#ffb460', 0.18, 1.6), shop: makeHalo(30, '#ffb060', 0.12, 1.6),
    neonBlue: makeHalo(14, '#6ad0ff', 0.18, 1.4), lamp: makeHalo(36, '#ffc070', 0.22, 1.7), lampCore: makeHalo(9, '#fff0c8', 0.45, 1.4),
    bbLamp: makeHalo(6, '#ffc878', 0.3, 1.6), bbWash: makeHalo(30, '#ffb0a0', 0.06, 1.5), phone: makeHalo(4, '#bfe4ff', 0.45, 1.2),
    car: makeHalo(4, '#ff4a3a', 0.5, 1.2), landing: makeHalo(6, '#ffffff', 0.4, 1.3), strobe: makeHalo(5, '#ffffff', 0.6, 1),
    heliPool: makeHalo(12, '#dfe8ff', 0.22, 1.4), fwFlash: makeHalo(60, '#ffe6c8', 0.12, 1.5), fwGlow: FW_COLS.map(p => makeHalo(40, p[1], 0.14, 1.6)),
  };
  SKY_C = renderSky(); MOON_C = renderMoon(); CLOUDS = CLOUD_DEFS.map(makeCloudLayer);
  HILLS_C = renderHills(); SIGN_C = renderSign(); OBS_R = renderObservatory(); CAP_R = renderCapitol();
  AVE_C = renderAvenue(); PALM_HEADS = renderPalmHeads(); GLOW_C = renderGlow(); FLATS_C = renderFlats();
  DINER_IN = renderDinerIn(); DINER_OUT = renderDinerOut(); PYLON_C = renderPylon(false); PYLON_OFF = renderPylon(true);
  SHOP_C = renderShop(); STOP_C = renderStop(); BB_C = renderBillboard(); BB_AD = renderBillboardAd();
  PITCH = makePitch(BB.w, BB.h, { accent: '#cc701f' });
  CAR_C = renderCar(); STREET_C = renderStreet(); VIG_C = renderVignette(); SBEAM_C = renderSearchBeam();
  PUFF_C = [null]; for (let r = 1; r <= 5; r++) PUFF_C.push(makePuff(r, '#8a7c84', '#6a5e6a'));
  prerenderTraffic();
}

/* ================= Frame compositor (back to front) ================= */
function drawScroll(cv, y, k, t) {
  const off = Math.floor(frac(k * t / LOOP) * W);
  ctx.drawImage(cv, off - W, y); ctx.drawImage(cv, off, y);
}
function drawBillboard(t, pitch) {
  halo(HL.bbWash, BB.x + (BB.w >> 1), BB.y + 6, 0.9);
  ctx.drawImage(BB_C, 0, 0);
  if (!PITCH.covers(t, pitch)) ctx.drawImage(BB_AD, BB.x, BB.y);
  PITCH.draw(ctx, t, BB.x, BB.y, pitch);
  for (const lx of BB_LAMPS) { rect(ctx, lx - 1, BB_LENS, 3, 1, '#f2dcaa'); halo(HL.bbLamp, lx, BB_LENS + 1, 0.95); }
}
// pitch: the billboard's face; billboard: false leaves it out (the loop videos); weather: a pick (site/js/engine/weather.js),
// the city's own when left out; crowd: the share of the people to show, 0..1 (CROWD when left out), 0 = nobody
function renderFrame(t, { pitch = false, billboard = true, weather, reducedMotion = false, crowd: share } = {}) {
  t = ((t % LOOP) + LOOP) % LOOP;
  ctx.setTransform(1, 0, 0, 1, 0, 0); ctx.globalAlpha = 1; ctx.globalCompositeOperation = 'source-over';
  const wx = WEATHER(weather), wxc = billboard ? WX_BB : WX;
  const crowd = clamp(typeof share === 'number' && share === share ? share : CROWD, 0, 1);
  const here = rank => crowd > 0 && (rank === 0 || rank < crowd);
  // sky: stars, the moon, clouds, the plane
  ctx.drawImage(SKY_C, 0, 0);
  for (const s of STARS) {
    const a = s.b * (0.55 + 0.45 * wave(t, s.k, s.ph));
    if (s.big) { ctx.globalAlpha = a * 0.35; rect(ctx, s.x - 1, s.y, 3, 1, s.col); rect(ctx, s.x, s.y - 1, 1, 3, s.col); }
    ctx.globalAlpha = a; px(ctx, s.x, s.y, s.col);
  }
  ctx.globalAlpha = 1;
  halo(HL.moon, MOON.x, MOON.y); halo(HL.moonCore, MOON.x, MOON.y);
  ctx.drawImage(MOON_C, MOON.x - MOON.r, MOON.y - MOON.r);
  drawPlane(t);
  for (const L of CLOUDS) drawScroll(L.c, L.y, L.k, t);
  // the hills, their lights and masts, the sign, the observatory
  ctx.drawImage(HILLS_C, 0, 0);
  for (const h of HOUSE) if (h.tw) { ctx.globalAlpha = 0.55 + 0.45 * wave(t, h.k, h.ph); rect(ctx, h.x, h.y, h.w, 1, h.col); }
  ctx.globalAlpha = 1;
  for (const l of ROAD_LAMPS) halo(HL.roadLamp, l.x, l.y, 0.9);
  for (const [mx, my, ph] of [[LEE.x + 2, LEE.y - 21, 0], [LEE.x + 2, LEE.y - 12, 0.5], [LEE.x - 5, LEE.y - 13, 0.25]]) {
    const on = frac(t / 2 + ph) < 0.4;
    px(ctx, mx, my, on ? '#ff4a4a' : '#5a1a1e'); if (on) halo(HL.red, mx, my, 0.85);
  }
  const breathe = 0.94 + 0.06 * wave(t, 8);
  halo(HL.sign, (SIGN.x + SIGN_X1) / 2, SIGN.y + 7, breathe); halo(HL.signCore, (SIGN.x + SIGN_X1) / 2, SIGN.y + 6, breathe);
  ctx.drawImage(SIGN_C, SIGN.x, SIGN.y);
  drawSignLight(t);
  halo(HL.obs, OBS.cx, OBS.base - 10, 1);
  ctx.drawImage(OBS_R.c, OBS_R.x, OBS_R.y);
  halo(HL.obsDoor, OBS.cx, OBS.base - 4, 0.8);
  drawObservatoryLife(t, here);
  drawHeli(t);
  drawFireworks(t, reducedMotion);
  drawSearchlights(t);
  drawWeather(ctx, wx.shared, 0, t, wxc, reducedMotion);
  ctx.globalCompositeOperation = 'lighter'; ctx.drawImage(GLOW_C, 0, 0); ctx.globalCompositeOperation = 'source-over';
  // Hollywood's mid-rises, Capitol Records and its Morse beacon
  ctx.drawImage(FLATS_C, 0, 0);
  for (const w of FLAT_WINS) {
    if (w.beacon) { const on = frac(t / 2.5 + w.x / 97) < 0.35; px(ctx, w.x, w.y, on ? '#ff4040' : '#5a1a1e'); if (on) halo(HL.red, w.x, w.y, 0.8); continue; }
    if (hash(w.id, Math.floor(frac(t / LOOP + w.off) * w.n), 3) < 0.55) px(ctx, w.x, w.y, w.col);
  }
  halo(HL.capitol, CAP.cx, CAP.roof + 30, 1);
  ctx.drawImage(CAP_R.c, CAP_R.x, CAP_R.y);
  const beacon = morseOn(t);
  px(ctx, CAP.cx, CAP.roof - 32, beacon ? '#ff4040' : '#6a1a1e'); if (beacon) halo(HL.red, CAP.cx, CAP.roof - 32, 1);
  if (wx.leaves) drawLeaves(0, t);
  // the side street: its cars and lamps, the palms receding
  ctx.drawImage(AVE_C, 0, 0);
  drawAvenueTraffic(t);
  for (const l of AV_LAMPS) { const [x, y] = proj(l.X - Math.sign(l.X) * 1.2, l.z, 7); px(ctx, x, y + 1, '#fff2c8'); halo(HL.avLamp, x, y + 1, Math.min(1, 40 / l.z)); }
  drawPalms(t, false);
  // the boulevard's north side: the record shop and its cat, the billboard, the diner and its people, the pylon
  ctx.drawImage(SHOP_C, 0, 0);
  drawCat(t);
  ctx.drawImage(STOP_C, 0, 0);
  halo(HL.shop, 392, 180, 1); halo(HL.neonBlue, 391, 170, 0.85 + 0.15 * wave(t, 160));
  if (billboard) drawBillboard(t, pitch);
  ctx.drawImage(DINER_IN, 0, 0);
  drawDinerLife(t, here);
  ctx.drawImage(DINER_OUT, 0, 0);
  drawDoor(t);
  halo(HL.diner, 50, 178, 1); halo(HL.diner, 100, 178, 0.7);
  drawVentSteam(t);
  const m = satLevel(t);
  ctx.drawImage(PYLON_C, 0, 0);
  drawPylonLife(t, m);
  halo(HL.saturn, SAT.x, SAT.y, 0.9); halo(HL.pink, 108, 106, 0.95 * m); halo(HL.aqua, 110, 120, 0.9);
  if (wx.leaves) drawLeaves(1, t);
  drawWeather(ctx, wx.shared, 1, t, wxc, reducedMotion);
  // the street: the asphalt's shine, people on the sidewalk, the corner palms, the parked convertible, the traffic
  ctx.drawImage(STREET_C, 0, 0);
  halo(HL.lamp, LAMP.x, LAMP.top + 3, 0.95);
  for (const gx of [LAMP.x - 6, LAMP.x + 6]) halo(HL.lampCore, gx, LAMP.top + 2, 0.9);
  reflect(ctx, t, { top: CURB + 3, rows: SOUTH - CURB - 4, squash: 1.7, alpha: 0.22, k1: 30, k2: 72, amp: 0.3, grow: 0.02 });
  streak(ctx, t, 108, 6, '#ff5aa0', 0.22 * m, 1, CURB + 2, SOUTH);
  streak(ctx, t, SAT.x, 4, '#ffa04a', 0.16, 2, CURB + 6, SOUTH);
  for (const [sx, k] of [[15, 3], [38, 4], [61, 5], [84, 6]]) streak(ctx, t, sx, 5, '#ffcf88', 0.12, k, CURB + 2, SOUTH);
  streak(ctx, t, LAMP.x, 3, '#ffd090', 0.2, 7, CURB + 2, SOUTH);
  streak(ctx, t, 392, 6, '#ffb870', 0.1, 8, CURB + 2, SOUTH);
  drawSidewalk(t, here);
  drawPalms(t, true);
  for (let j = 0; j < 5; j++) { ctx.globalAlpha = 0.28 * (1 - j / 5); ctx.drawImage(CAR_C, 0, 15 - j, 60, 1, 16, 199 + 18 + j, 60, 1); }
  ctx.globalAlpha = 0.6; rect(ctx, 18, 217, 56, 1, '#06050a'); ctx.globalAlpha = 1;
  ctx.drawImage(CAR_C, 16, 199);
  drawTraffic(t, here);
  if (wx.leaves) drawLeaves(2, t);
  drawWeather(ctx, wx.shared, 2, t, wxc, reducedMotion);
  ctx.drawImage(VIG_C, 0, 0);
}

export default {
  id: 'los-angeles',
  name: 'Los Angeles',
  tagline: 'Hollywood after dark',
  timeZone: 'America/Los_Angeles',
  accent: '#cc701f',
  focusX: 255,
  landmark: [205, 303],                                // the Hollywood Sign
  landmarkTop: 23,                                     // Mount Lee's summit over the sign
  billboard: BB,
  crowd: CROWD,                                        // the share of the people shown by default; render(t, { crowd }) thins them, 0 = nobody
  ambience: { traffic: 0.4, wind: 0.3, crickets: 0.25, crowd: 0.12 },
  events: [
    { t: 30, type: 'horn', x: 200, gain: 0.12 },
    { t: BUS.arrive - 0.5, type: 'horn', x: 440, gain: 0.16 },
    { t: CRUISE.t0 + 7, type: 'horn', x: 230, gain: 0.18 },
    { t: DOME.tele, type: 'chime', x: 436, gain: 0.22 },
    ...BURSTS.map(b => ({ t: b.t, type: 'firework', x: b.x, gain: 0.5 })),
  ],
  create(c) {
    ctx = c;
    prerender();
    return { render: renderFrame };
  },
};
